
import React, { useState, useEffect } from 'react';
import { Incident, RiskAssessment, PermitToWork, ManagementOfChange, AuditInspection, ActionItem, MocAction, MocApproval, TitanDocument, DocStatus, DocCategory, DocVersion, DocApproval } from '../../types';
import { fetchIncidents, createIncident, updateIncident, fetchRisks, createRisk, updateRisk, fetchPermits, createPermit, updatePermit, fetchMocs, createMoc, updateMoc, fetchAudits, createAudit, updateAudit, fetchActions, createAction, updateAction, fetchLookups } from '../../services/apiService';
import { 
  ShieldAlert, AlertTriangle, FileText, CheckCircle, Search, History, 
  Activity, X, Plus, Filter, Calendar, ClipboardCheck, Lock, ArrowRight,
  AlertOctagon, CheckSquare, Clock, Flame, Users, FileCheck, ThumbsUp, PenTool,
  ArrowLeft, FileSignature, UserCheck, ListTodo, Sliders, ChevronDown, Upload,
  Eye, Archive, RotateCcw, Share2, BookOpen, Link, FileUp, Globe
} from 'lucide-react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend, BarChart, Bar, XAxis, YAxis, CartesianGrid } from 'recharts';

// --- CONSTANTS ---
const HSSE_LOOKUPS = {
  INCIDENT_STATUS: { OPEN: 1, INV: 2, ACT: 3, CLSD: 4 },
  AUDIT_STATUS: { SCHD: 1, PROG: 2, COMP: 3 },
  PERMIT_STATUS: { DRFT: 1, REQD: 2, AUTH: 3, ACTV: 4, SUSP: 5, CLSD: 6 },
  PERMIT_TYPE: { HOT: 1, CONF: 2, ELEC: 3, HGHT: 4, GEN: 5 },
  MOC_STATUS: { DRFT: 1, REVW: 2, APPR: 3, IMPL: 4, CLSD: 5, REJT: 6 },
  JHA_STATUS: { DRFT: 1, ACTV: 2, ARCH: 3 },
  ACTION_STATUS: { OPEN: 1, PROG: 2, HOLD: 3, CLSD: 4 },
  ACTION_PRIORITY: { LOW: 1, MED: 2, HIGH: 3 }
};

// --- MOCK DATA ---

const initialDocs: TitanDocument[] = [
  { 
    id: 'SOP-OPS-001', title: 'Compressor Service Procedure', category: 'SOP', department: 'Operations', owner: 'M. Scott', 
    currentVersion: '3.1', status: 'Active', 
    createdDate: '2023-01-10', lastReviewDate: '2024-03-01', nextReviewDate: '2025-03-01',
    confidentiality: 'Internal', acknowledgmentRequired: true, acknowledgmentCount: 45,
    history: [
      { version: '3.1', date: '2024-03-01', changedBy: 'J. Smith', changeSummary: 'Updated safety torque settings', fileUrl: 'sop-ops-001-v3.1.pdf' },
      { version: '3.0', date: '2023-01-10', changedBy: 'M. Scott', changeSummary: 'Complete rewrite for new ISO standard' }
    ],
    approvals: [
        { role: 'HSE Manager', name: 'S. Nkosi', status: 'Approved', date: '2024-02-28' },
        { role: 'Ops Lead', name: 'J. Halpert', status: 'Approved', date: '2024-03-01' }
    ]
  },
  { 
    id: 'POL-HSE-005', title: 'Working at Heights Policy', category: 'Policy', department: 'HSSE', owner: 'S. Nkosi', 
    currentVersion: '2.0', status: 'Review', 
    createdDate: '2022-06-15', lastReviewDate: '2023-11-15', nextReviewDate: '2023-11-15',
    confidentiality: 'Public', acknowledgmentRequired: true, acknowledgmentCount: 120,
    history: [
      { version: '2.0', date: '2022-06-15', changedBy: 'S. Nkosi', changeSummary: 'Added harness inspection checklist', fileUrl: 'pol-hse-005.pdf' }
    ],
    approvals: [
      { role: 'HSE Manager', name: 'S. Nkosi', status: 'Approved', date: '2024-05-20' },
      { role: 'Ops Director', name: 'D. Wallace', status: 'Pending' }
    ]
  },
  { 
    id: 'FRM-HR-012', title: 'Leave Application Form', category: 'Form', department: 'HR', owner: 'T. Flenderson', 
    currentVersion: '1.0', status: 'Active', 
    createdDate: '2022-01-20', lastReviewDate: '2022-01-20', nextReviewDate: '2025-01-20',
    confidentiality: 'Internal', acknowledgmentRequired: false, acknowledgmentCount: 0,
    history: [{ version: '1.0', date: '2022-01-20', changedBy: 'T. Flenderson', changeSummary: 'Initial Release', fileUrl: 'leave-form.docx' }],
    approvals: []
  },
  {
    id: 'SOP-ENG-099', title: 'VSD Calibration Guide', category: 'SOP', department: 'Engineering', owner: 'R. Howard',
    currentVersion: '0.1', status: 'Draft',
    createdDate: '2024-05-22', lastReviewDate: '', nextReviewDate: '',
    confidentiality: 'Restricted', acknowledgmentRequired: false, acknowledgmentCount: 0,
    history: [{ version: '0.1', date: '2024-05-22', changedBy: 'R. Howard', changeSummary: 'Initial Draft' }],
    approvals: []
  }
];

const COLORS = ['#cd8832', '#3b82f6', '#ef4444', '#10b981', '#8b5cf6', '#ec4899', '#f59e0b'];

// --- HELPER COMPONENTS ---

const Modal = ({ title, onClose, children, size = 'lg' }: { title: string, onClose: (e?: any) => void, children: React.ReactNode, size?: 'md' | 'lg' | 'xl' }) => (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4 animate-fade-in">
        <div className={`bg-slate-800 rounded-lg border border-slate-700 w-full shadow-2xl flex flex-col max-h-[95vh] ${size === 'xl' ? 'max-w-6xl' : size === 'lg' ? 'max-w-4xl' : 'max-w-2xl'}`}>
            <div className="p-4 border-b border-slate-700 flex justify-between items-center bg-slate-900 rounded-t-lg">
                <h3 className="text-xl font-bold text-white">{title}</h3>
                <button onClick={onClose} className="text-slate-400 hover:text-white"><X className="w-5 h-5"/></button>
            </div>
            <div className="flex-1 overflow-y-auto p-6 scrollbar-thin scrollbar-thumb-slate-700 scrollbar-track-slate-900">
                {children}
            </div>
        </div>
    </div>
);

const Input = ({ label, className, ...props }: any) => (
    <div className="mb-4">
        <label className="block text-xs text-slate-400 uppercase font-bold mb-1">{label}</label>
        <input className={`w-full bg-slate-900 border border-slate-700 text-white p-2 rounded focus:border-titan-500 outline-none [&::-webkit-calendar-picker-indicator]:invert ${className}`} {...props} />
    </div>
);

const Checkbox = ({ label, ...props }: any) => (
  <label className="flex items-center gap-2 cursor-pointer p-2 hover:bg-slate-750 rounded transition">
    <input type="checkbox" className="w-4 h-4 rounded bg-slate-800 border-slate-600 text-titan-500 focus:ring-titan-500" {...props} />
    <span className="text-sm text-slate-300">{label}</span>
  </label>
);

const Select = ({ label, options, ...props }: any) => (
    <div className="mb-4">
        <label className="block text-xs text-slate-400 uppercase font-bold mb-1">{label}</label>
        <select className="w-full bg-slate-900 border border-slate-700 text-white p-2 rounded focus:border-titan-500 outline-none" {...props}>
            {options.map((o: any) => {
                const code = typeof o === 'string' ? o : o.code;
                const label = typeof o === 'string' ? o : o.label;
                return <option key={code} value={code}>{label}</option>;
            })}
        </select>
    </div>
);

const RiskBadge = ({ level }: { level: string }) => {
    const colors: any = { 'Low': 'bg-green-500', 'Medium': 'bg-yellow-500', 'High': 'bg-orange-500', 'Extreme': 'bg-red-600', 'Critical': 'bg-red-600' };
    return <span className={`${colors[level] || 'bg-slate-500'} text-white text-xs px-2 py-1 rounded font-bold uppercase`}>{level}</span>;
}

const StatusBadge = ({ status }: { status: string }) => {
    const colors: any = { 
        'Open': 'text-blue-400 bg-blue-900/30', 'Investigating': 'text-orange-400 bg-orange-900/30', 'Closed': 'text-slate-400 bg-slate-700/50',
        'Active': 'text-green-400 bg-green-900/30', 'Draft': 'text-slate-400 bg-slate-700', 'Review': 'text-purple-400 bg-purple-900/30',
        'Authorized': 'text-blue-400 bg-blue-900/30', 'Requested': 'text-yellow-400 bg-yellow-900/30', 'Suspended': 'text-red-400 bg-red-900/30',
        'Approved': 'text-green-400 bg-green-900/30', 'Implementation': 'text-blue-400 bg-blue-900/30', 'Rejected': 'text-red-400 bg-red-900/30',
        'Archived': 'text-slate-400 bg-slate-800', 'In Progress': 'text-orange-400 bg-orange-900/30', 'On Hold': 'text-yellow-400 bg-yellow-900/30',
        'Scheduled': 'text-blue-400 bg-blue-900/30', 'Completed': 'text-green-400 bg-green-900/30', 'Pending Approval': 'text-yellow-400 bg-yellow-900/30'
    };
    return <span className={`${colors[status] || 'text-white bg-slate-700'} px-2 py-1 rounded text-xs font-bold`}>{status}</span>;
}

// --- MAIN VIEW ---

export const HSSEMgrView: React.FC = () => {
  const [tab, setTab] = useState<'Dashboard' | 'Incidents' | 'Risk' | 'PTW' | 'MOC' | 'Audits' | 'Actions'>('Dashboard');
  
  // Data State
  const [incidents, setIncidents] = useState<Incident[]>([]);
  const [risks, setRisks] = useState<RiskAssessment[]>([]);
  const [permits, setPermits] = useState<PermitToWork[]>([]);
  const [mocs, setMocs] = useState<ManagementOfChange[]>([]);
  const [audits, setAudits] = useState<AuditInspection[]>([]);
  const [actions, setActions] = useState<ActionItem[]>([]);
  const [lookups, setLookups] = useState<Record<string, any[]>>({});
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Helper to get label from code
  const getLookupLabel = (lookupName: string, code: any) => {
    const options = lookups[lookupName];
    if (!Array.isArray(options)) return code;
    
    const found = options.find((o: any) => {
        const itemCode = typeof o === 'string' ? o : o.code;
        return String(itemCode) === String(code);
    });
    if (!found) return code;
    return typeof found === 'string' ? found : (found as any).label;
  };

  // Dynamic KPI Calculations
  const daysWithoutIncident = React.useMemo(() => {
    if (incidents.length === 0) return 'N/A';
    
    // Sort incidents by date descending
    const sorted = [...incidents].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
    const latest = new Date(sorted[0].date);
    const today = new Date();
    
    const diffTime = Math.abs(today.getTime() - latest.getTime());
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24)); 
    
    // If the incident is in the future (bad data), return 0
    if (latest > today) return 0;
    
    return diffDays;
  }, [incidents]);

  const ltifr = React.useMemo(() => {
    // LTIFR = (Number of LTI incidents * 1,000,000) / Man-hours worked
    // For now, we assume a placeholder man-hours or just count LTIs
    const ltiCount = incidents.filter(inc => Number(inc.type) === 5).length;
    // placeholder man-hours logic: 200,000 per month roughly
    const estimatedManHours = 1000000; 
    return ((ltiCount * 1000000) / estimatedManHours).toFixed(2);
  }, [incidents]);

  // Dynamic Chart Data
  const chartData = React.useMemo(() => {
    const counts: Record<string, number> = {};
    incidents.forEach(inc => {
      const label = getLookupLabel('incident_type', inc.type);
      counts[label] = (counts[label] || 0) + 1;
    });
    return Object.entries(counts).map(([name, value]) => ({ name, value }));
  }, [incidents, lookups]);

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        const [incidentsData, risksData, permitsData, mocsData, auditsData, actionsData, lookupsData] = await Promise.all([
          fetchIncidents(),
          fetchRisks(),
          fetchPermits(),
          fetchMocs(),
          fetchAudits(),
          fetchActions(),
          fetchLookups('HSSE_SAFETY')
        ]);
        setIncidents(incidentsData);
        setRisks(risksData);
        setPermits(permitsData);
        setMocs(mocsData);
        setAudits(auditsData);
        setActions(actionsData);
        setLookups(lookupsData);
      } catch (err: any) {
        setError(err.message || 'Failed to fetch data');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  // Modals
  const [showIncidentModal, setShowIncidentModal] = useState(false);
  const [showPTWModal, setShowPTWModal] = useState(false);
  const [showMocModal, setShowMocModal] = useState(false);
  const [showJhaModal, setShowJhaModal] = useState(false);
  const [showActionModal, setShowActionModal] = useState(false);
  const [showAuditModal, setShowAuditModal] = useState(false);

  // Selection State
  const [selectedPermit, setSelectedPermit] = useState<PermitToWork | null>(null);
  const [selectedMoc, setSelectedMoc] = useState<ManagementOfChange | null>(null);
  const [selectedJha, setSelectedJha] = useState<RiskAssessment | null>(null);
  const [selectedAction, setSelectedAction] = useState<ActionItem | null>(null);
  const [selectedIncident, setSelectedIncident] = useState<Incident | null>(null);
  const [selectedAudit, setSelectedAudit] = useState<AuditInspection | null>(null);
  
  // Wizard State
  const [ptwModalTab, setPtwModalTab] = useState<'Details' | 'Hazards' | 'Controls' | 'Auth'>('Details');
  const [mocModalTab, setMocModalTab] = useState<'Definition' | 'Impact' | 'Plan' | 'Auth'>('Definition');
  const [jhaModalTab, setJhaModalTab] = useState<'Scope' | 'Hazards' | 'Risk' | 'Controls' | 'Review'>('Scope');
  
  const ptwTabs: Array<'Details' | 'Hazards' | 'Controls' | 'Auth'> = ['Details', 'Hazards', 'Controls', 'Auth'];
  const mocTabs: Array<'Definition' | 'Impact' | 'Plan' | 'Auth'> = ['Definition', 'Impact', 'Plan', 'Auth'];
  const jhaTabs: Array<'Scope' | 'Hazards' | 'Risk' | 'Controls' | 'Review'> = ['Scope', 'Hazards', 'Risk', 'Controls', 'Review'];
  
  const currentPtwIndex = ptwTabs.indexOf(ptwModalTab);
  const currentMocIndex = mocTabs.indexOf(mocModalTab);
  const currentJhaIndex = jhaTabs.indexOf(jhaModalTab);

  const tabs = [
      { id: 'Dashboard', icon: Activity },
      { id: 'Incidents', icon: AlertTriangle },
      { id: 'Risk', icon: ShieldAlert },
      { id: 'PTW', icon: Lock },
      { id: 'MOC', icon: History },
      { id: 'Audits', icon: ClipboardCheck },
      { id: 'Actions', icon: CheckSquare },
  ];

  // --- Handlers ---

  const handleOpenIncident = (inc?: Incident) => {
      setSelectedIncident(inc || null);
      setShowIncidentModal(true);
  };

  const handleSaveIncident = async (e: React.FormEvent) => {
      e.preventDefault();
      const formData = new FormData(e.target as HTMLFormElement);
      const cat = formData.get('category');
      const typ = formData.get('type');
      const sev = formData.get('severity');
      const stat = formData.get('status');

      const incidentData: Partial<Incident> = {
          category: cat ? Number(cat) : 1,
          type: typ ? Number(typ) : 1,
          severity: sev ? Number(sev) : 1,
          description: formData.get('description') as string,
          location: formData.get('location') as string,
          date: formData.get('date') as string,
          status: stat ? Number(stat) : (selectedIncident?.status || HSSE_LOOKUPS.INCIDENT_STATUS.OPEN),
          reportedBy: selectedIncident?.reportedBy || 'Current User'
      };
      
      try {
          if (selectedIncident && !selectedIncident.id.startsWith('Auto-')) {
              const updated = await updateIncident(selectedIncident.id, incidentData);
              setIncidents(incidents.map(i => i.id === updated.id ? updated : i));
          } else {
              const created = await createIncident({
                  ...incidentData,
                  id: `INC-${Date.now().toString().slice(-4)}` // Simplified ID gen
              });
              setIncidents([created, ...incidents]);
          }
          setShowIncidentModal(false);
          setSelectedIncident(null);
      } catch (err) {
          alert('Failed to save incident');
      }
  };

  const handleOpenAudit = (audit?: AuditInspection) => {
      setSelectedAudit(audit || {
          id: `AUD-${Date.now().toString().slice(-4)}`,
          type: 1,
          auditor: 'Current User',
          date: new Date().toISOString().split('T')[0],
          findings: 0,
          status: HSSE_LOOKUPS.AUDIT_STATUS.SCHD
      } as AuditInspection);
      setShowAuditModal(true);
  };

  const handleSaveAudit = async (e: React.FormEvent) => {
      e.preventDefault();
      if (!selectedAudit) return;
      const formData = new FormData(e.target as HTMLFormElement);
      const typ = formData.get('type');
      const stat = formData.get('status');

      const auditData: Partial<AuditInspection> = {
          type: typ ? Number(typ) : 1,
          auditor: formData.get('auditor') as string,
          date: formData.get('date') as string,
          status: stat ? Number(stat) : 1,
          score: formData.get('score') ? Number(formData.get('score')) : undefined,
          findings: Number(formData.get('findings')) || 0
      };

      try {
          const exists = audits.find(a => a.id === selectedAudit.id);
          if (exists) {
              const updated = await updateAudit(selectedAudit.id, auditData);
              setAudits(audits.map(a => a.id === updated.id ? updated : a));
          } else {
              const created = await createAudit({ ...auditData, id: selectedAudit.id });
              setAudits([...audits, created]);
          }
          setShowAuditModal(false);
      } catch (err) {
          alert('Failed to save audit');
      }
  };

  const handleOpenPermit = (permit?: PermitToWork) => {
      setSelectedPermit(permit || {
          id: `PTW-${Date.now().toString().slice(-4)}`,
          type: 1,
          location: '',
          contractor: '',
          validFrom: new Date().toISOString().slice(0, 16),
          validTo: '',
          status: HSSE_LOOKUPS.PERMIT_STATUS.DRFT
      } as PermitToWork);
      setShowPTWModal(true);
      setPtwModalTab('Details');
  };

  const handlePtwStatusChange = async (newStatus: number) => {
      if (!selectedPermit) return;
      
      const ptwData: Partial<PermitToWork> = { status: newStatus };
      if (newStatus === HSSE_LOOKUPS.PERMIT_STATUS.ACTV) ptwData.authorizedBy = 'Current User (Admin)';
      
      try {
          const exists = permits.find(p => p.id === selectedPermit.id);
          if (exists) {
              const updated = await updatePermit(selectedPermit.id, ptwData);
              setPermits(permits.map(p => p.id === updated.id ? updated : p));
              setSelectedPermit(updated);
          } else {
              const created = await createPermit({ ...selectedPermit, ...ptwData });
              setPermits([created, ...permits]);
              setSelectedPermit(created);
          }
          
          if (newStatus === HSSE_LOOKUPS.PERMIT_STATUS.CLSD || newStatus === HSSE_LOOKUPS.PERMIT_STATUS.REQD) {
              setShowPTWModal(false);
          }
      } catch (err) {
          alert('Failed to update permit status');
      }
  };

  const handleOpenMoc = (moc?: ManagementOfChange) => {
      setSelectedMoc(moc || {
          id: `MOC-${Date.now().toString().slice(-4)}`,
          title: '',
          type: 1,
          initiator: 'Current User',
          dateRaised: new Date().toISOString().split('T')[0],
          riskLevel: 1,
          status: HSSE_LOOKUPS.MOC_STATUS.DRFT,
          approvals: [
              { role: 'HSE Lead', name: 'Safety Officer', status: 'Pending' },
              { role: 'Ops Manager', name: 'Plant Manager', status: 'Pending' }
          ],
          actions: []
      } as ManagementOfChange);
      setShowMocModal(true);
      setMocModalTab('Definition');
  };

  const handleMocStatusChange = async (newStatus: number) => {
      if (!selectedMoc) return;
      const mocData: Partial<ManagementOfChange> = { status: newStatus };
      
      try {
          const exists = mocs.find(m => m.id === selectedMoc.id);
          if (exists) {
              const updated = await updateMoc(selectedMoc.id, mocData);
              setMocs(mocs.map(m => m.id === updated.id ? updated : m));
              setSelectedMoc(updated);
          } else {
              const created = await createMoc({ ...selectedMoc, ...mocData });
              setMocs([created, ...mocs]);
              setSelectedMoc(created);
          }
          
          if (newStatus === HSSE_LOOKUPS.MOC_STATUS.REVW || newStatus === HSSE_LOOKUPS.MOC_STATUS.CLSD) {
             if(newStatus === HSSE_LOOKUPS.MOC_STATUS.REVW) setShowMocModal(false); 
          }
      } catch (err) {
          alert('Failed to update MOC status');
      }
  };

  const handleMocApproval = async (role: string, decision: 'Approved' | 'Rejected') => {
      if (!selectedMoc || !selectedMoc.approvals) return;
      
      const updatedApprovals = selectedMoc.approvals.map(app => 
          app.role === role ? { ...app, status: decision, date: new Date().toISOString().split('T')[0] } : app
      );
      
      let mocData: Partial<ManagementOfChange> = { approvals: updatedApprovals };
      
      // Check if all approved to auto-promote
      if (decision === 'Approved' && updatedApprovals.every(a => a.status === 'Approved')) {
          mocData.status = HSSE_LOOKUPS.MOC_STATUS.APPR;
      } else if (decision === 'Rejected') {
          mocData.status = HSSE_LOOKUPS.MOC_STATUS.REJT;
      }

      try {
          const updated = await updateMoc(selectedMoc.id, mocData);
          setMocs(mocs.map(m => m.id === updated.id ? updated : m));
          setSelectedMoc(updated);
      } catch (err) {
          alert('Failed to approve MOC');
      }
  };
  
  // --- JHA Handlers ---
  const handleOpenJha = (jha?: RiskAssessment) => {
    setSelectedJha(jha || {
        id: `RA-${Date.now().toString().slice(-4)}`,
        taskName: '',
        department: '',
        hazards: [],
        initialRisk: 'Low',
        controls: [],
        residualRisk: 'Low',
        reviewDate: new Date().toISOString().split('T')[0],
        status: HSSE_LOOKUPS.JHA_STATUS.DRFT,
        ppe: [],
        teamMembers: ''
    } as RiskAssessment);
    setShowJhaModal(true);
    setJhaModalTab('Scope');
  };

  const handleUpdateJha = async (updates: Partial<RiskAssessment>) => {
      if(!selectedJha) return;
      
      try {
          const exists = risks.find(r => r.id === selectedJha.id);
          if(exists) {
              const updated = await updateRisk(selectedJha.id, updates);
              setRisks(risks.map(r => r.id === updated.id ? updated : r));
              setSelectedJha(updated);
          } else {
              const created = await createRisk({ ...selectedJha, ...updates });
              setRisks([...risks, created]);
              setSelectedJha(created);
          }
      } catch (err) {
          alert('Failed to update JHA');
      }
  };

  const handleJhaRiskSelect = (type: 'Initial' | 'Residual', val: string) => {
      if (type === 'Initial') handleUpdateJha({ initialRisk: val as any });
      else handleUpdateJha({ residualRisk: val as any });
  };

  const handleToggleHazard = (hazard: string) => {
      if(!selectedJha) return;
      const current = selectedJha.hazards || [];
      const updated = current.includes(hazard) 
        ? current.filter(h => h !== hazard) 
        : [...current, hazard];
      handleUpdateJha({ hazards: updated });
  }
  
  const handleTogglePPE = (item: string) => {
      if(!selectedJha) return;
      const current = selectedJha.ppe || [];
      const updated = current.includes(item) 
        ? current.filter(p => p !== item) 
        : [...current, item];
      handleUpdateJha({ ppe: updated });
  }

  // --- ACTION TRACKER HANDLERS ---
  const handleOpenAction = (action?: ActionItem) => {
      setSelectedAction(action || {
          id: `ACT-${Date.now().toString().slice(-4)}`,
          source: 1,
          description: '',
          assignee: '',
          raisedBy: 'Current User',
          raisedDate: new Date().toISOString().split('T')[0],
          dueDate: '',
          priority: HSSE_LOOKUPS.ACTION_PRIORITY.MED,
          status: HSSE_LOOKUPS.ACTION_STATUS.OPEN
      } as ActionItem);
      setShowActionModal(true);
  };

  const handleSaveAction = async (e: React.FormEvent) => {
      e.preventDefault();
      if(!selectedAction) return;
      
      try {
          const exists = actions.find(a => a.id === selectedAction.id);
          if(exists) {
              const updated = await updateAction(selectedAction.id, selectedAction);
              setActions(actions.map(a => a.id === updated.id ? updated : a));
          } else {
              const created = await createAction(selectedAction);
              setActions([...actions, created]);
          }
          setShowActionModal(false);
      } catch (err) {
          alert('Failed to save action');
      }
  };

  const handleUpdateActionState = (updates: Partial<ActionItem>) => {
      if(!selectedAction) return;
      setSelectedAction({...selectedAction, ...updates});
  }

  const handleVerifyAction = (decision: 'Accepted' | 'Rejected') => {
      if(!selectedAction) return;
      if(decision === 'Accepted') {
          handleUpdateActionState({ 
              status: HSSE_LOOKUPS.ACTION_STATUS.CLSD, 
              closeOutStatus: 'Accepted', 
              verifiedBy: 'Current User', 
              verificationDate: new Date().toISOString().split('T')[0] 
          });
      } else {
          handleUpdateActionState({ 
              status: HSSE_LOOKUPS.ACTION_STATUS.PROG, 
              closeOutStatus: 'Rejected' 
          });
          alert("Action Rejected. Status reverted to 'In Progress'. Please add comments.");
      }
  }

  // Nav Handlers
  const handleNextPtw = () => currentPtwIndex < ptwTabs.length - 1 && setPtwModalTab(ptwTabs[currentPtwIndex + 1]);
  const handleBackPtw = () => currentPtwIndex > 0 && setPtwModalTab(ptwTabs[currentPtwIndex - 1]);
  const handleNextMoc = () => currentMocIndex < mocTabs.length - 1 && setMocModalTab(mocTabs[currentMocIndex + 1]);
  const handleBackMoc = () => currentMocIndex > 0 && setMocModalTab(mocTabs[currentMocIndex - 1]);
  const handleNextJha = () => currentJhaIndex < jhaTabs.length - 1 && setJhaModalTab(jhaTabs[currentJhaIndex + 1]);
  const handleBackJha = () => currentJhaIndex > 0 && setJhaModalTab(jhaTabs[currentJhaIndex - 1]);

  const HAZARDS = [
      "Pinch points", "Obstruction", "Slip / trip hazards", "Falling objects", "Uneven surfaces",
      "Chemicals", "Fumes", "Hot surfaces /steam", "Moving parts", "Flying pieces",
      "Space limitations", "Noise", "Heavy equipment", "Ignition sources", "Energy sources"
  ];

  const PPE = [
      "Hard hat", "Safety overall", "Safety boots", "Cut resistant gloves", "Heat resistant gloves",
      "Chemical gloves", "Welding gloves", "Nitrile gloves", "Safety glasses", "Ear plugs/muffs",
      "Face mask", "Respiratory mask", "Face shield", "Safety harness"
  ];

  // Helper for interactive risk matrix
  const InteractiveRiskMatrix = ({ selected, onSelect }: { selected: string, onSelect: (val: string) => void }) => {
      const levels = [
          ['M', 'H', 'E'],
          ['L', 'M', 'H'],
          ['L', 'L', 'M']
      ];
      const mappings: Record<string, string> = { 'L': 'Low', 'M': 'Medium', 'H': 'High', 'E': 'Extreme' };
      const colors: Record<string, string> = { 'L': 'bg-green-500', 'M': 'bg-yellow-500', 'H': 'bg-orange-500', 'E': 'bg-red-600' };

      return (
          <div className="flex flex-col items-center">
              <div className="grid grid-cols-3 gap-1 w-64">
                  {levels.flat().map((lvl, idx) => {
                      const fullLabel = mappings[lvl];
                      const isSelected = selected === fullLabel;
                      return (
                          <button
                            key={idx}
                            onClick={() => onSelect(fullLabel)}
                            className={`h-16 flex items-center justify-center font-bold text-slate-900 transition border-4 
                                ${colors[lvl]} 
                                ${isSelected ? 'border-white scale-105 shadow-xl z-10' : 'border-transparent opacity-80 hover:opacity-100'}
                            `}
                          >
                              {lvl}
                          </button>
                      );
                  })}
              </div>
              <div className="flex justify-between w-64 text-xs text-slate-400 mt-2">
                 <span>Severity &rarr;</span>
                 <span>Probability &darr;</span>
              </div>
              <div className="mt-4 text-center">
                  <span className="text-slate-400 text-sm">Selected Risk: </span>
                  <span className={`font-bold ml-2 ${selected === 'Extreme' || selected === 'High' ? 'text-red-500' : selected === 'Medium' ? 'text-yellow-500' : 'text-green-500'}`}>
                      {selected || 'None'}
                  </span>
              </div>
          </div>
      );
  }

  return (
    <div>
      
      {/* Navigation Tabs */}
      <div className="flex justify-between items-center mb-6 min-h-[4rem]">
        <div className="flex border-b border-slate-800 overflow-x-auto w-full">
            {tabs.map(t => (
                <button 
                  key={t.id}
                  onClick={() => setTab(t.id as any)}
                  className={`flex items-center gap-2 px-6 py-4 border-b-2 font-medium transition whitespace-nowrap ${
                      tab === t.id ? 'border-titan-500 text-titan-500' : 'border-transparent text-slate-400 hover:text-white'
                  }`}
                >
                    <t.icon className="w-4 h-4" /> {t.id === 'Risk' ? 'Risk & JHA' : t.id}
                </button>
            ))}
        </div>
      </div>

      {loading && (
        <div className="flex items-center justify-center p-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-titan-500"></div>
          <span className="ml-4 text-slate-400">Loading safety data...</span>
        </div>
      )}

      {error && (
        <div className="bg-red-900/20 border border-red-900/50 p-4 rounded-lg mb-6 flex items-center gap-3 text-red-400">
          <AlertTriangle className="w-5 h-5" />
          <div>
            <div className="font-bold">Error Loading Data</div>
            <div className="text-sm opacity-80">{error}</div>
          </div>
        </div>
      )}

      {!loading && !error && (
        <>
      {/* --- DASHBOARD TAB --- */}
      {tab === 'Dashboard' && (
        <div className="space-y-6 animate-fade-in">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="bg-slate-800 p-4 rounded border-t-4 border-green-500">
                    <div className="text-slate-400 text-xs uppercase">LTIFR (12m Rolling)</div>
                    <div className="text-3xl font-bold text-white">{ltifr}</div>
                    <div className="text-green-500 text-xs">Target: 0.00</div>
                </div>
                <div className="bg-slate-800 p-4 rounded border-t-4 border-titan-500">
                    <div className="text-slate-400 text-xs uppercase">Days Without Incident</div>
                    <div className="text-3xl font-bold text-white">{daysWithoutIncident}</div>
                </div>
                <div className="bg-slate-800 p-4 rounded border-t-4 border-blue-500">
                    <div className="text-slate-400 text-xs uppercase">Open Actions</div>
                    <div className="text-3xl font-bold text-white">{actions.filter(a => a.status !== HSSE_LOOKUPS.ACTION_STATUS.CLSD).length}</div>
                </div>
                <button onClick={() => handleOpenIncident()} className="bg-red-600 hover:bg-red-700 text-white rounded p-4 flex flex-col items-center justify-center gap-1 transition shadow-lg shadow-red-900/20">
                    <AlertTriangle className="w-6 h-6" />
                    <span className="font-bold">REPORT INCIDENT</span>
                </button>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="lg:col-span-2 bg-slate-800 rounded-lg border border-slate-700 p-6">
                    <h3 className="text-lg font-bold text-white mb-4">Recent Incidents</h3>
                    <div className="space-y-3">
                        {incidents.map(inc => (
                        <div key={inc.id} onClick={() => handleOpenIncident(inc)} className="bg-slate-900 p-4 rounded border-l-4 border-titan-500 flex justify-between items-center cursor-pointer hover:bg-slate-800 transition">
                            <div>
                                <div className="flex items-center gap-2 mb-1">
                                    <span className="text-xs font-mono text-slate-500">{inc.id}</span>
                                    <RiskBadge level={getLookupLabel('incident_severity', inc.severity)} />
                                </div>
                                <div className="text-white font-medium">{getLookupLabel('incident_category', inc.category)} - {getLookupLabel('incident_type', inc.type)}: {inc.description}</div>
                                <div className="text-xs text-slate-500 mt-1">{inc.location} • Reported by {inc.reportedBy}</div>
                            </div>
                            <div className="text-right">
                                <div className="text-sm text-slate-400">{inc.date}</div>
                                <StatusBadge status={getLookupLabel('incident_status', inc.status)} />
                            </div>
                        </div>
                        ))}
                    </div>
                </div>
                
                <div className="lg:col-span-1 bg-slate-800 rounded-lg border border-slate-700 p-6">
                    <h3 className="text-lg font-bold text-white mb-4">Incident Types (YTD)</h3>
                    <div className="h-64">
                        <ResponsiveContainer width="100%" height="100%">
                            <PieChart>
                            <Pie data={chartData} innerRadius={60} outerRadius={80} paddingAngle={5} dataKey="value">
                                {chartData.map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                ))}
                            </Pie>
                            <Tooltip contentStyle={{backgroundColor: '#1e293b', borderColor: '#334155'}} />
                            <Legend />
                            </PieChart>
                        </ResponsiveContainer>
                    </div>
                </div>
            </div>
        </div>
      )}

      {/* --- INCIDENTS TAB --- */}
      {tab === 'Incidents' && (
          <div className="space-y-4 animate-fade-in">
              <div className="flex justify-between items-center">
                  <h2 className="text-xl font-bold text-white">Incident & Event Management</h2>
                  <button onClick={() => handleOpenIncident()} className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded font-bold flex items-center gap-2">
                      <Plus className="w-4 h-4" /> Log Incident
                  </button>
              </div>
              <div className="bg-slate-800 rounded-lg border border-slate-700 overflow-hidden">
                  <table className="w-full text-left">
                      <thead className="bg-slate-900 text-slate-400 text-xs uppercase">
                          <tr>
                              <th className="px-6 py-4">ID</th>
                              <th className="px-6 py-4">Date</th>
                              <th className="px-6 py-4">Category</th>
                              <th className="px-6 py-4">Type</th>
                              <th className="px-6 py-4">Description</th>
                              <th className="px-6 py-4">Severity</th>
                              <th className="px-6 py-4">Status</th>
                              <th className="px-6 py-4"></th>
                          </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-700">
                          {incidents.map(inc => (
                              <tr key={inc.id} onClick={() => handleOpenIncident(inc)} className="hover:bg-slate-750 cursor-pointer">
                                  <td className="px-6 py-4 font-mono text-slate-400">{inc.id}</td>
                                  <td className="px-6 py-4 text-white">{inc.date}</td>
                                  <td className="px-6 py-4 text-slate-300">{getLookupLabel('incident_category', inc.category)}</td>
                                  <td className="px-6 py-4 text-slate-300">{getLookupLabel('incident_type', inc.type)}</td>
                                  <td className="px-6 py-4 text-slate-300 truncate max-w-xs">{inc.description}</td>
                                  <td className="px-6 py-4"><RiskBadge level={getLookupLabel('incident_severity', inc.severity)} /></td>
                                  <td className="px-6 py-4"><StatusBadge status={getLookupLabel('incident_status', inc.status)} /></td>
                                  <td className="px-6 py-4 text-right"><button className="text-titan-500 hover:text-white text-sm">View</button></td>
                              </tr>
                          ))}
                      </tbody>
                  </table>
              </div>
          </div>
      )}

      {/* --- RISK & JHA TAB --- */}
      {tab === 'Risk' && (
          <div className="space-y-4 animate-fade-in">
              <div className="flex justify-between items-center">
                  <h2 className="text-xl font-bold text-white">Risk Management & JHA</h2>
                  <button onClick={() => handleOpenJha()} className="bg-titan-500 hover:bg-titan-600 text-white px-4 py-2 rounded font-bold flex items-center gap-2">
                      <Plus className="w-4 h-4" /> Create JHA
                  </button>
              </div>
              
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  {/* Risk Registry */}
                  <div className="lg:col-span-2 bg-slate-800 rounded-lg border border-slate-700 overflow-hidden">
                      <table className="w-full text-left">
                          <thead className="bg-slate-900 text-slate-400 text-xs uppercase">
                              <tr>
                                  <th className="px-6 py-4">Ref</th>
                                  <th className="px-6 py-4">Task / Activity</th>
                                  <th className="px-6 py-4">Dept</th>
                                  <th className="px-6 py-4">Initial Risk</th>
                                  <th className="px-6 py-4">Residual</th>
                                  <th className="px-6 py-4">Status</th>
                              </tr>
                          </thead>
                          <tbody className="divide-y divide-slate-700">
                              {risks.map(risk => (
                                  <tr key={risk.id} onClick={() => handleOpenJha(risk)} className="hover:bg-slate-750 cursor-pointer">
                                      <td className="px-6 py-4 font-mono text-slate-400">{risk.id}</td>
                                      <td className="px-6 py-4 text-white font-medium">{risk.taskName}</td>
                                      <td className="px-6 py-4 text-slate-400">{risk.department}</td>
                                      <td className="px-6 py-4"><RiskBadge level={risk.initialRisk} /></td>
                                      <td className="px-6 py-4"><RiskBadge level={risk.residualRisk} /></td>
                                      <td className="px-6 py-4"><StatusBadge status={getLookupLabel('jha_status', risk.status)} /></td>
                                  </tr>
                              ))}
                          </tbody>
                      </table>
                  </div>

                  {/* Visual Risk Matrix */}
                  <div className="bg-slate-800 rounded-lg border border-slate-700 p-6 flex flex-col items-center justify-center">
                      <h3 className="text-white font-bold mb-4">Risk Matrix</h3>
                      <div className="grid grid-cols-3 gap-1 w-full max-w-xs">
                          {/* Row 1 */}
                          <div className="bg-yellow-500 h-16 flex items-center justify-center font-bold text-slate-900">M</div>
                          <div className="bg-orange-500 h-16 flex items-center justify-center font-bold text-slate-900">H</div>
                          <div className="bg-red-600 h-16 flex items-center justify-center font-bold text-white">E</div>
                          {/* Row 2 */}
                          <div className="bg-green-500 h-16 flex items-center justify-center font-bold text-slate-900">L</div>
                          <div className="bg-yellow-500 h-16 flex items-center justify-center font-bold text-slate-900">M</div>
                          <div className="bg-orange-500 h-16 flex items-center justify-center font-bold text-slate-900">H</div>
                          {/* Row 3 */}
                          <div className="bg-green-500 h-16 flex items-center justify-center font-bold text-slate-900">L</div>
                          <div className="bg-green-500 h-16 flex items-center justify-center font-bold text-slate-900">L</div>
                          <div className="bg-yellow-500 h-16 flex items-center justify-center font-bold text-slate-900">M</div>
                      </div>
                      <div className="text-xs text-slate-500 mt-4 text-center">Standard 3x3 Risk Matrix<br/>(Severity x Probability)</div>
                  </div>
              </div>
          </div>
      )}

      {/* --- PERMIT TO WORK TAB --- */}
      {tab === 'PTW' && (
          <div className="space-y-4 animate-fade-in">
              <div className="flex justify-between items-center">
                  <h2 className="text-xl font-bold text-white">Permit to Work (PTW)</h2>
                  <button onClick={() => handleOpenPermit()} className="bg-titan-500 hover:bg-titan-600 text-white px-4 py-2 rounded font-bold flex items-center gap-2">
                      <Plus className="w-4 h-4" /> Request Permit
                  </button>
              </div>

              {/* PTW List View */}
              <div className="bg-slate-800 rounded-lg border border-slate-700 overflow-hidden">
                <table className="w-full text-left">
                  <thead className="bg-slate-900 text-slate-400 text-xs uppercase">
                    <tr>
                      <th className="px-6 py-4">Permit ID</th>
                      <th className="px-6 py-4">Type</th>
                      <th className="px-6 py-4">Location</th>
                      <th className="px-6 py-4">Contractor</th>
                      <th className="px-6 py-4">Valid From / To</th>
                      <th className="px-6 py-4">Status</th>
                      <th className="px-6 py-4">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-700">
                    {permits.map(ptw => (
                      <tr key={ptw.id} className="hover:bg-slate-750">
                        <td className="px-6 py-4 font-mono text-slate-300">{ptw.id}</td>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-2">
                             {Number(ptw.type) === HSSE_LOOKUPS.PERMIT_TYPE.HOT && <Flame className="w-4 h-4 text-red-500"/>}
                             {Number(ptw.type) === HSSE_LOOKUPS.PERMIT_TYPE.CONF && <AlertOctagon className="w-4 h-4 text-orange-500"/>}
                             {Number(ptw.type) === HSSE_LOOKUPS.PERMIT_TYPE.ELEC && <Activity className="w-4 h-4 text-yellow-500"/>}
                             <span className="text-white font-medium">{getLookupLabel('permit_type', ptw.type)}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4 text-slate-300">{ptw.location}</td>
                        <td className="px-6 py-4 text-slate-300">{ptw.contractor}</td>
                        <td className="px-6 py-4 text-xs text-slate-400">
                          <div>{ptw.validFrom}</div>
                          <div>{ptw.validTo}</div>
                        </td>
                        <td className="px-6 py-4"><StatusBadge status={getLookupLabel('permit_status', ptw.status)} /></td>
                        <td className="px-6 py-4">
                          <button onClick={() => handleOpenPermit(ptw)} className="text-titan-500 hover:text-white text-sm font-medium">View / Edit</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
          </div>
      )}

      {/* --- MOC TAB --- */}
      {tab === 'MOC' && (
          <div className="space-y-4 animate-fade-in">
              <div className="flex justify-between items-center">
                  <h2 className="text-xl font-bold text-white">Management of Change (MOC)</h2>
                  <button onClick={() => handleOpenMoc()} className="bg-titan-500 hover:bg-titan-600 text-white px-4 py-2 rounded font-bold flex items-center gap-2">
                      <Plus className="w-4 h-4" /> Raise Change
                  </button>
              </div>
              <div className="bg-slate-800 rounded-lg border border-slate-700 overflow-hidden">
                <table className="w-full text-left">
                  <thead className="bg-slate-900 text-slate-400 text-xs uppercase">
                    <tr>
                      <th className="px-6 py-4">MOC ID</th>
                      <th className="px-6 py-4">Title</th>
                      <th className="px-6 py-4">Type</th>
                      <th className="px-6 py-4">Risk</th>
                      <th className="px-6 py-4">Initiator / Date</th>
                      <th className="px-6 py-4">Status</th>
                      <th className="px-6 py-4">Progress</th>
                      <th className="px-6 py-4"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-700">
                    {mocs.map(moc => (
                      <tr key={moc.id} className="hover:bg-slate-750">
                          <td className="px-6 py-4 font-mono text-slate-400">{moc.id}</td>
                          <td className="px-6 py-4 text-white font-medium">{moc.title}</td>
                          <td className="px-6 py-4 text-slate-300">{getLookupLabel('moc_type', moc.type)}</td>
                          <td className="px-6 py-4"><RiskBadge level={getLookupLabel('moc_risk_level', moc.riskLevel)} /></td>
                          <td className="px-6 py-4 text-xs text-slate-400">
                             <div>{moc.initiator}</div>
                             <div>{moc.dateRaised}</div>
                          </td>
                          <td className="px-6 py-4"><StatusBadge status={getLookupLabel('moc_status', moc.status)} /></td>
                          <td className="px-6 py-4">
                             {/* Simple Progress Bar Logic based on status */}
                             <div className="w-24 bg-slate-700 rounded-full h-1.5">
                               <div className={`h-1.5 rounded-full ${
                                 Number(moc.status) === HSSE_LOOKUPS.MOC_STATUS.CLSD ? 'bg-green-500 w-full' : 
                                 Number(moc.status) === HSSE_LOOKUPS.MOC_STATUS.IMPL ? 'bg-blue-500 w-3/4' : 
                                 Number(moc.status) === HSSE_LOOKUPS.MOC_STATUS.APPR ? 'bg-purple-500 w-1/2' : 
                                 Number(moc.status) === HSSE_LOOKUPS.MOC_STATUS.REVW ? 'bg-orange-500 w-1/4' : 'bg-slate-500 w-1/12'
                               }`}></div>
                             </div>
                          </td>
                          <td className="px-6 py-4 text-right">
                             <button onClick={() => handleOpenMoc(moc)} className="text-titan-500 hover:text-white text-sm font-medium">Manage</button>
                          </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
          </div>
      )}

      {/* --- AUDITS TAB --- */}
      {tab === 'Audits' && (
          <div className="space-y-4 animate-fade-in">
              <div className="flex justify-between items-center">
                  <h2 className="text-xl font-bold text-white">Audits & Inspections</h2>
                  <button onClick={() => handleOpenAudit()} className="bg-titan-500 hover:bg-titan-600 text-white px-4 py-2 rounded font-bold flex items-center gap-2">
                      <Plus className="w-4 h-4" /> Schedule Audit
                  </button>
              </div>
              <div className="bg-slate-800 rounded-lg border border-slate-700 overflow-hidden">
                  <table className="w-full text-left">
                      <thead className="bg-slate-900 text-slate-400 text-xs uppercase">
                          <tr>
                              <th className="px-6 py-4">ID</th>
                              <th className="px-6 py-4">Type</th>
                              <th className="px-6 py-4">Auditor</th>
                              <th className="px-6 py-4">Date</th>
                              <th className="px-6 py-4">Score</th>
                              <th className="px-6 py-4">Findings</th>
                              <th className="px-6 py-4">Status</th>
                          </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-700">
                          {audits.map(audit => (
                              <tr key={audit.id} onClick={() => handleOpenAudit(audit)} className="hover:bg-slate-750 cursor-pointer">
                                  <td className="px-6 py-4 font-mono text-slate-400">{audit.id}</td>
                                  <td className="px-6 py-4 text-white font-medium">{getLookupLabel('audit_type', audit.type)}</td>
                                  <td className="px-6 py-4 text-slate-400">{audit.auditor}</td>
                                  <td className="px-6 py-4 text-slate-300">{audit.date}</td>
                                  <td className="px-6 py-4 font-bold text-white">{audit.score ? `${audit.score}%` : '-'}</td>
                                  <td className="px-6 py-4"><span className="bg-red-900/50 text-red-400 px-2 py-1 rounded text-xs font-bold">{audit.findings}</span></td>
                                  <td className="px-6 py-4"><StatusBadge status={getLookupLabel('audit_status', audit.status)} /></td>
                              </tr>
                          ))}
                      </tbody>
                  </table>
              </div>
          </div>
      )}

      {/* --- ACTIONS TAB --- */}
      {tab === 'Actions' && (
          <div className="space-y-4 animate-fade-in">
              <div className="flex justify-between items-center">
                  <h2 className="text-xl font-bold text-white">Action Tracker</h2>
                  <div className="flex gap-4 items-center">
                      <div className="flex gap-2">
                          <div className="bg-slate-800 px-3 py-1 rounded text-sm text-slate-300 border border-slate-700">Total Open: <span className="font-bold text-white">{actions.filter(a => a.status !== HSSE_LOOKUPS.ACTION_STATUS.CLSD).length}</span></div>
                          <div className="bg-red-900/30 px-3 py-1 rounded text-sm text-red-300 border border-red-900/50">Overdue: <span className="font-bold text-white">{actions.filter(a => a.status !== HSSE_LOOKUPS.ACTION_STATUS.CLSD && new Date(a.dueDate) < new Date()).length}</span></div>
                      </div>
                      <button onClick={() => handleOpenAction()} className="bg-titan-500 hover:bg-titan-600 text-white px-4 py-2 rounded font-bold flex items-center gap-2">
                          <Plus className="w-4 h-4" /> Raise Action
                      </button>
                  </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {[
                    { label: 'Open', value: HSSE_LOOKUPS.ACTION_STATUS.OPEN },
                    { label: 'In Progress', value: HSSE_LOOKUPS.ACTION_STATUS.PROG },
                    { label: 'Closed', value: HSSE_LOOKUPS.ACTION_STATUS.CLSD }
                  ].map(statusGroup => (
                      <div key={statusGroup.value} className="bg-slate-800/50 p-4 rounded-lg border border-slate-700 min-h-[400px]">
                          <h4 className="text-slate-400 font-bold uppercase text-xs mb-4 flex justify-between">
                              {statusGroup.label}
                              <span className="bg-slate-700 text-white px-2 rounded">{actions.filter(a => a.status === statusGroup.value || (statusGroup.value === HSSE_LOOKUPS.ACTION_STATUS.OPEN && a.status === HSSE_LOOKUPS.ACTION_STATUS.HOLD)).length}</span>
                          </h4>
                          <div className="space-y-3">
                              {actions.filter(a => a.status === statusGroup.value || (statusGroup.value === HSSE_LOOKUPS.ACTION_STATUS.OPEN && a.status === HSSE_LOOKUPS.ACTION_STATUS.HOLD)).map(action => (
                                  <div key={action.id} onClick={() => handleOpenAction(action)} className="bg-slate-900 p-3 rounded border border-slate-700 hover:border-titan-500 transition cursor-pointer group">
                                      <div className="flex justify-between items-start mb-2">
                                          <span className="text-xs font-mono text-slate-500">{action.id}</span>
                                          <span className={`text-[10px] uppercase font-bold px-1.5 py-0.5 rounded ${Number(action.priority) === 3 ? 'bg-red-500 text-white' : 'bg-slate-700 text-slate-300'}`}>{getLookupLabel('action_priority', action.priority)}</span>
                                      </div>
                                      <p className="text-sm text-white font-medium mb-2 group-hover:text-titan-400 transition">{action.description}</p>
                                      <div className="flex justify-between items-end text-xs text-slate-400 border-t border-slate-800 pt-2">
                                          <div>
                                              <div>Src: {getLookupLabel('action_source', action.source)}</div>
                                              <div>Assigned: {action.assignee}</div>
                                          </div>
                                          <div className={`${new Date(action.dueDate) < new Date() && Number(action.status) !== HSSE_LOOKUPS.ACTION_STATUS.CLSD ? 'text-red-400 font-bold' : ''}`}>
                                              {action.dueDate}
                                          </div>
                                      </div>
                                  </div>
                              ))}
                          </div>
                      </div>
                  ))}
              </div>
          </div>
      )}

      {/* --- MODALS --- */}
      {showIncidentModal && (
          <Modal title={selectedIncident ? `Manage Incident: ${selectedIncident.id}` : "Log HSSE Incident"} onClose={() => setShowIncidentModal(false)}>
              <form onSubmit={handleSaveIncident} className="space-y-4">
                  <div className="bg-slate-900 p-3 rounded border border-slate-700 mb-4 flex justify-between">
                      <div>
                        <div className="text-xs text-slate-500 uppercase font-bold">Incident ID</div>
                        <div className="text-white font-mono">{selectedIncident?.id || 'Auto-Generated'}</div>
                      </div>
                      {selectedIncident && (
                          <div className="text-right">
                              <div className="text-xs text-slate-500 uppercase font-bold">Status</div>
                              <StatusBadge status={getLookupLabel('incident_status', selectedIncident.status)} />
                          </div>
                      )}
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                      <Select 
                        label="Incident Category" 
                        name="category" 
                        defaultValue={selectedIncident?.category} 
                        options={lookups['incident_category'] || ['Safety', 'Health', 'Environmental', 'Security', 'Process Safety', 'Quality']} 
                        required 
                      />
                      <Select 
                        label="Incident Type" 
                        name="type" 
                        defaultValue={selectedIncident?.type} 
                        options={lookups['incident_type'] || [
                          'Near Miss', 'First Aid', 'Medical Treatment', 'Restricted Work', 
                          'Lost Time Injury (LTI)', 'Fatality', 'Environmental Spill', 
                          'Fire / Explosion', 'Equipment Damage', 'Process Safety Event (PSE)', 
                          'Unsafe Act', 'Unsafe Condition', 'Positive Observation', 'Vehicle'
                        ]} 
                        required 
                      />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                       <Select 
                        label="Severity" 
                        name="severity" 
                        defaultValue={selectedIncident?.severity} 
                        options={lookups['incident_severity'] || []} 
                        required 
                       />
                       <Input label="Date & Time" name="date" type="datetime-local" defaultValue={selectedIncident?.date} required />
                  </div>

                  <Input label="Location" name="location" defaultValue={selectedIncident?.location} placeholder="e.g. Workshop Bay 3" required />
                  
                  {/* Description and Actions */}
                  <div className="mb-4">
                      <label className="block text-xs text-slate-400 uppercase font-bold mb-1">Description of Incident <span className="text-red-500">*</span></label>
                      <textarea name="description" defaultValue={selectedIncident?.description} className="w-full bg-slate-900 border border-slate-700 text-white p-2 rounded focus:border-titan-500 outline-none h-24" required></textarea>
                  </div>
                  
                  {selectedIncident && (
                      <div className="mb-4">
                          <label className="block text-xs text-slate-400 uppercase font-bold mb-1">Update Status</label>
                          <select name="status" defaultValue={selectedIncident.status} className="w-full bg-slate-900 border border-slate-700 text-white p-2 rounded focus:border-titan-500 outline-none">
                              {(lookups['incident_status'] || []).map((opt: any) => (
                                <option key={opt.code} value={opt.code}>{opt.label}</option>
                              ))}
                          </select>
                      </div>
                  )}

                  <div className="flex justify-end pt-4">
                      <button type="submit" className="bg-red-600 hover:bg-red-700 text-white font-bold py-2 px-6 rounded transition">
                          {selectedIncident ? 'Update Incident' : 'Submit Incident Report'}
                      </button>
                  </div>
              </form>
          </Modal>
      )}

      {showAuditModal && selectedAudit && (
          <Modal title={selectedAudit.id ? `Manage Audit: ${selectedAudit.id}` : "Schedule New Audit"} onClose={() => setShowAuditModal(false)}>
              <form onSubmit={handleSaveAudit} className="space-y-4">
                  <div className="bg-slate-900 p-3 rounded border border-slate-700 mb-4">
                      <div className="text-xs text-slate-500 uppercase font-bold">Audit Reference</div>
                      <div className="text-white font-mono">{selectedAudit.id}</div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                      <Select 
                        label="Type" 
                        name="type" 
                        defaultValue={selectedAudit.type} 
                        options={lookups['audit_type'] || []} 
                        required 
                      />
                      <Input label="Scheduled Date" name="date" type="date" defaultValue={selectedAudit.date} required />
                  </div>

                  <Input label="Auditor / Inspector" name="auditor" defaultValue={selectedAudit.auditor} />

                  <div className="grid grid-cols-2 gap-4">
                      <Select 
                        label="Status" 
                        name="status" 
                        defaultValue={selectedAudit.status} 
                        options={lookups['audit_status'] || []} 
                      />
                      <Input label="Score (%)" name="score" type="number" defaultValue={selectedAudit.score} />
                  </div>
                  
                  <Input label="Total Findings / Non-Conformances" name="findings" type="number" defaultValue={selectedAudit.findings} />

                  <div className="flex justify-end pt-4">
                      <button type="submit" className="bg-titan-500 hover:bg-titan-600 text-white font-bold py-2 px-6 rounded transition">
                          {Number(selectedAudit.status) === HSSE_LOOKUPS.AUDIT_STATUS.SCHD ? 'Schedule Audit' : 'Update Audit Record'}
                      </button>
                  </div>
              </form>
          </Modal>
      )}

      {showActionModal && selectedAction && (
          <Modal title={`Action Register: ${selectedAction.id}`} onClose={() => setShowActionModal(false)} size="xl">
              <form onSubmit={handleSaveAction} className="space-y-8 px-2">
                  
                  {/* Section 1: Identification */}
                  <div>
                      <h3 className="text-lg font-bold text-white border-b border-slate-700 pb-2 mb-4">1. Action Identification</h3>
                      <div className="grid grid-cols-2 gap-4">
                          <Input label="Action Reference No." value={selectedAction.id} disabled />
                          <div className="grid grid-cols-2 gap-4">
                              <Select 
                                label="Source of Action" 
                                value={selectedAction.source} 
                                onChange={(e: any) => handleUpdateActionState({source: Number(e.target.value)})} 
                                options={lookups['action_source'] || []}
                              />
                              <Select 
                                label="Priority" 
                                value={selectedAction.priority} 
                                onChange={(e: any) => handleUpdateActionState({priority: Number(e.target.value)})} 
                                options={lookups['action_priority'] || []}
                              />
                          </div>
                      </div>
                      <div className="mt-4">
                          <label className="block text-xs text-slate-400 uppercase font-bold mb-1">Action Description (What must be done)</label>
                          <textarea 
                            className="w-full bg-slate-900 border border-slate-700 text-white p-2 rounded focus:border-titan-500 outline-none h-20"
                            value={selectedAction.description}
                            onChange={(e) => handleUpdateActionState({description: e.target.value})}
                          ></textarea>
                      </div>
                      <div className="mt-4">
                          <Input 
                            label="Reason for Action / Risk Addressed" 
                            value={selectedAction.reason || ''} 
                            onChange={(e: any) => handleUpdateActionState({reason: e.target.value})} 
                            placeholder="e.g. Prevent recurrence of crane slip"
                          />
                      </div>
                  </div>

                  {/* Section 2: Responsibility */}
                  <div>
                      <h3 className="text-lg font-bold text-white border-b border-slate-700 pb-2 mb-4">2. Responsibility & Timing</h3>
                      <div className="grid grid-cols-2 gap-4">
                          <Input 
                            label="Action Raised By" 
                            value={selectedAction.raisedBy} 
                            onChange={(e: any) => handleUpdateActionState({raisedBy: e.target.value})} 
                          />
                          <Input 
                            label="Date Raised" 
                            type="date" 
                            value={selectedAction.raisedDate} 
                            onChange={(e: any) => handleUpdateActionState({raisedDate: e.target.value})} 
                          />
                      </div>
                      <div className="bg-slate-900/50 p-4 rounded border border-slate-700 mt-2">
                          <div className="grid grid-cols-2 gap-4">
                              <Input 
                                label="Action Owner (Responsible Person)" 
                                value={selectedAction.assignee} 
                                onChange={(e: any) => handleUpdateActionState({assignee: e.target.value})} 
                                placeholder="Single accountable person"
                              />
                              <Input 
                                label="Target Due Date" 
                                type="date" 
                                value={selectedAction.dueDate} 
                                onChange={(e: any) => handleUpdateActionState({dueDate: e.target.value})} 
                              />
                          </div>
                          <div className="text-xs text-slate-500 mt-2 italic flex items-center gap-1">
                              <AlertTriangle className="w-3 h-3 text-titan-500" /> Only one person may be accountable per action.
                          </div>
                      </div>
                  </div>

                  {/* Section 3: Tracking */}
                  <div>
                      <h3 className="text-lg font-bold text-white border-b border-slate-700 pb-2 mb-4">3. Status Tracking</h3>
                      <div className="grid grid-cols-2 gap-4">
                          <Select 
                            label="Current Status" 
                            value={selectedAction.status} 
                            onChange={(e: any) => handleUpdateActionState({status: Number(e.target.value)})} 
                            options={lookups['action_status'] || []}
                            disabled={Number(selectedAction.status) === HSSE_LOOKUPS.ACTION_STATUS.CLSD} 
                          />
                          <Input 
                            label="Revised Due Date (if applicable)" 
                            type="date" 
                            value={selectedAction.revisedDueDate || ''} 
                            onChange={(e: any) => handleUpdateActionState({revisedDueDate: e.target.value})} 
                          />
                      </div>
                      <div className="mt-4">
                          <label className="block text-xs text-slate-400 uppercase font-bold mb-1">Progress Comments / Updates</label>
                          <textarea 
                            className="w-full bg-slate-900 border border-slate-700 text-white p-2 rounded focus:border-titan-500 outline-none h-20"
                            value={selectedAction.progressComments || ''}
                            onChange={(e) => handleUpdateActionState({progressComments: e.target.value})}
                          ></textarea>
                      </div>
                  </div>

                  {/* Section 4: Close Out & Verification */}
                  <div className={`p-4 rounded border ${Number(selectedAction.status) === HSSE_LOOKUPS.ACTION_STATUS.CLSD ? 'bg-green-900/10 border-green-900/50' : 'bg-slate-900 border-slate-700'}`}>
                      <h3 className="text-lg font-bold text-white mb-4">4. Close-Out & Verification</h3>
                      
                      {/* Evidence Input - Required before verification */}
                      <div className="mb-4">
                          <label className="block text-xs text-slate-400 uppercase font-bold mb-1">Evidence of Close-Out <span className="text-red-500">*</span></label>
                          <div className="flex gap-2">
                              <input 
                                className="flex-1 bg-slate-800 border border-slate-600 text-white p-2 rounded" 
                                placeholder="Enter link to photo, document reference, etc."
                                value={selectedAction.evidence || ''}
                                onChange={(e) => handleUpdateActionState({evidence: e.target.value})}
                                disabled={Number(selectedAction.status) === HSSE_LOOKUPS.ACTION_STATUS.CLSD}
                              />
                          </div>
                          <div className="text-xs text-slate-500 mt-1">Actions may only be closed when the control has been implemented and evidence provided.</div>
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                          <Input 
                             label="Verified By" 
                             value={selectedAction.verifiedBy || ''} 
                             disabled 
                             placeholder="Pending Verification"
                          />
                          <Input 
                             label="Verification Date" 
                             value={selectedAction.verificationDate || ''} 
                             disabled 
                          />
                      </div>

                      {Number(selectedAction.status) !== HSSE_LOOKUPS.ACTION_STATUS.CLSD && (
                          <div className="mt-6 border-t border-slate-700 pt-4 flex justify-between items-center">
                              <div className="text-sm text-slate-400">
                                  Is the evidence sufficient to close this action?
                              </div>
                              <div className="flex gap-3">
                                  <button 
                                    type="button"
                                    onClick={() => handleVerifyAction('Rejected')}
                                    className="px-4 py-2 bg-red-600/20 text-red-400 hover:bg-red-600 hover:text-white rounded font-bold transition border border-red-600/50"
                                  >
                                      Reject
                                  </button>
                                  <button 
                                    type="button"
                                    disabled={!selectedAction.evidence}
                                    onClick={() => handleVerifyAction('Accepted')}
                                    className={`px-4 py-2 rounded font-bold transition flex items-center gap-2 ${!selectedAction.evidence ? 'bg-slate-700 text-slate-500 cursor-not-allowed' : 'bg-green-600 hover:bg-green-500 text-white shadow-lg shadow-green-900/20'}`}
                                  >
                                      <CheckCircle className="w-4 h-4" /> Verify & Close
                                  </button>
                              </div>
                          </div>
                      )}

                      {Number(selectedAction.status) === HSSE_LOOKUPS.ACTION_STATUS.CLSD && (
                          <div className="mt-4 flex items-center justify-center gap-2 text-green-500 font-bold bg-green-900/20 p-3 rounded">
                              <CheckCircle className="w-5 h-5" /> Action Verified & Closed
                          </div>
                      )}
                  </div>

                  <div className="flex justify-end pt-4 border-t border-slate-700">
                      <button type="submit" className="bg-titan-500 hover:bg-titan-600 text-white px-6 py-2 rounded font-bold transition">Save Action Register</button>
                  </div>
              </form>
          </Modal>
      )}

      {showPTWModal && (
          <Modal title={selectedPermit ? `Permit Details: ${selectedPermit.id}` : "New Permit to Work"} onClose={() => setShowPTWModal(false)} size="xl">
              <div className="flex gap-6 h-[700px]">
                  {/* Left Sidebar Navigation */}
                  <div className="w-64 bg-slate-900 border-r border-slate-700 rounded-l-lg flex flex-col">
                      <button onClick={()=>setPtwModalTab('Details')} className={`text-left px-6 py-4 border-l-4 transition ${ptwModalTab==='Details' ? 'border-titan-500 bg-slate-800 text-white' : 'border-transparent text-slate-400 hover:bg-slate-800'}`}>
                          <div className="font-bold text-sm">1. General Details</div>
                          <div className="text-xs opacity-70">Scope, Time, Location</div>
                      </button>
                      <button onClick={()=>setPtwModalTab('Hazards')} className={`text-left px-6 py-4 border-l-4 transition ${ptwModalTab==='Hazards' ? 'border-titan-500 bg-slate-800 text-white' : 'border-transparent text-slate-400 hover:bg-slate-800'}`}>
                          <div className="font-bold text-sm">2. Hazards & PPE</div>
                          <div className="text-xs opacity-70">Risk Assessment</div>
                      </button>
                      <button onClick={()=>setPtwModalTab('Controls')} className={`text-left px-6 py-4 border-l-4 transition ${ptwModalTab==='Controls' ? 'border-titan-500 bg-slate-800 text-white' : 'border-transparent text-slate-400 hover:bg-slate-800'}`}>
                          <div className="font-bold text-sm">3. Critical Controls</div>
                          <div className="text-xs opacity-70">LOTO & Hot Work</div>
                      </button>
                      <button onClick={()=>setPtwModalTab('Auth')} className={`text-left px-6 py-4 border-l-4 transition ${ptwModalTab==='Auth' ? 'border-titan-500 bg-slate-800 text-white' : 'border-transparent text-slate-400 hover:bg-slate-800'}`}>
                          <div className="font-bold text-sm">4. Authorization</div>
                          <div className="text-xs opacity-70">Sign-off & Handover</div>
                      </button>
                      
                      <div className="mt-auto p-4 border-t border-slate-700">
                          <div className="text-xs text-slate-500 mb-1">Status</div>
                          <StatusBadge status={selectedPermit?.status ? getLookupLabel('permit_status', selectedPermit.status) : 'Draft'} />
                      </div>
                  </div>

                  {/* Main Form Content */}
                  <div className="flex-1 overflow-y-auto pr-2 flex flex-col">
                      <div className="flex-1">
                        {ptwModalTab === 'Details' && (
                            <div className="space-y-6 animate-fade-in">
                                <h3 className="text-lg font-bold text-white border-b border-slate-700 pb-2">Section 1: Permit Details</h3>
                                    <div className="grid grid-cols-2 gap-4">
                                        <Select label="Permit Type" options={lookups['permit_type'] || []} defaultValue={selectedPermit?.type} />
                                        <Input label="Permit Number" value={selectedPermit?.id || "Auto-Generated"} disabled />
                                    </div>
                                <div className="grid grid-cols-2 gap-4">
                                    <Input label="Valid From (Date & Time)" type="datetime-local" defaultValue={selectedPermit?.validFrom} />
                                    <Input label="Valid To (Date & Time)" type="datetime-local" defaultValue={selectedPermit?.validTo} />
                                </div>
                                <div className="grid grid-cols-2 gap-4">
                                    <Input label="Company Name" defaultValue={selectedPermit?.contractor} />
                                    <Input label="Area / Location" defaultValue={selectedPermit?.location} />
                                </div>
                                <div className="space-y-4">
                                    <div>
                                        <label className="block text-xs text-slate-400 uppercase font-bold mb-1">Scope of Work</label>
                                        <textarea className="w-full bg-slate-900 border border-slate-700 text-white p-2 rounded focus:border-titan-500 outline-none h-20" defaultValue={selectedPermit?.scope}></textarea>
                                    </div>
                                    <div>
                                        <label className="block text-xs text-slate-400 uppercase font-bold mb-1">Tools to be used</label>
                                        <textarea className="w-full bg-slate-900 border border-slate-700 text-white p-2 rounded focus:border-titan-500 outline-none h-16" defaultValue={selectedPermit?.tools}></textarea>
                                    </div>
                                    <Input label="Equipment to be worked on" defaultValue={selectedPermit?.equipment} />
                                </div>
                            </div>
                        )}

                        {ptwModalTab === 'Hazards' && (
                            <div className="space-y-8 animate-fade-in">
                                <div>
                                    <h3 className="text-lg font-bold text-white border-b border-slate-700 pb-2 mb-4">Section 2: Hazard Identification</h3>
                                    <div className="grid grid-cols-2 md:grid-cols-3 gap-y-3 gap-x-6">
                                        {HAZARDS.map(h => (
                                            <Checkbox key={h} label={h} defaultChecked={selectedPermit?.hazards?.includes(h)} />
                                        ))}
                                    </div>
                                </div>

                                <div>
                                    <h3 className="text-lg font-bold text-white border-b border-slate-700 pb-2 mb-4">Section 3: Life Saving Rules</h3>
                                    <div className="bg-slate-900 p-4 rounded border border-slate-700 text-center text-slate-400 text-sm">
                                        (Life Saving Rule Icons would appear here as per PDF)
                                    </div>
                                </div>

                                <div>
                                    <h3 className="text-lg font-bold text-white border-b border-slate-700 pb-2 mb-4">Section 5: Required PPE</h3>
                                    <div className="grid grid-cols-2 md:grid-cols-3 gap-y-3 gap-x-6">
                                        {PPE.map(p => (
                                            <Checkbox key={p} label={p} defaultChecked={selectedPermit?.ppe?.includes(p)} />
                                        ))}
                                    </div>
                                </div>
                            </div>
                        )}

                        {ptwModalTab === 'Controls' && (
                            <div className="space-y-8 animate-fade-in">
                                <div className="bg-slate-900/50 p-4 rounded border border-slate-700">
                                    <h3 className="text-lg font-bold text-white mb-4">Section 4: Emergency Prep</h3>
                                    <div className="grid grid-cols-2 gap-4">
                                        <Checkbox label="Escape route identified?" />
                                        <Checkbox label="Safety shower identified?" />
                                        <Input label="Emergency Comm Device" placeholder="Radio / Cell" />
                                        <Input label="Fire Extinguisher No." />
                                    </div>
                                </div>

                                <div className="bg-slate-900/50 p-4 rounded border border-slate-700">
                                    <h3 className="text-lg font-bold text-white mb-4">Section 6: Lock Out / Tag Out (LOTO)</h3>
                                    <div className="space-y-4">
                                        <div className="flex gap-8">
                                            <Checkbox label="Is Energy Isolation Required?" defaultChecked={selectedPermit?.lotoRequired} />
                                            <Checkbox label="Equipment Locked Out?" />
                                        </div>
                                        <div className="grid grid-cols-2 gap-4">
                                            <Input label="Lock Out / Tag Out No." defaultValue={selectedPermit?.lotoNumber} />
                                            <Input label="Isolation Points" />
                                        </div>
                                        <Checkbox label="Method to verify zero-energy state (try-out test) conducted?" />
                                    </div>
                                </div>

                                <div className="bg-red-900/10 p-4 rounded border border-red-900/30">
                                    <div className="flex items-center gap-2 mb-4">
                                        <Flame className="w-5 h-5 text-red-500" />
                                        <h3 className="text-lg font-bold text-white">Section 7 & 8: Hot Work & Fire Watch</h3>
                                    </div>
                                    <div className="space-y-4">
                                        <div className="grid grid-cols-2 gap-4">
                                            <Input label="O2 Level %" placeholder="19.5 - 23%" />
                                            <Input label="Air Monitor #" />
                                        </div>
                                        <div className="grid grid-cols-2 gap-y-2">
                                            <Checkbox label="Area Barricaded?" />
                                            <Checkbox label="Combustibles Removed?" />
                                            <Checkbox label="Ventilation Sufficient?" />
                                            <Checkbox label="Fire Watch Appointed?" defaultChecked={!!selectedPermit?.fireWatchName} />
                                        </div>
                                        <Input label="Fire Watch Name" defaultValue={selectedPermit?.fireWatchName} />
                                    </div>
                                </div>
                            </div>
                        )}

                        {ptwModalTab === 'Auth' && (
                            <div className="space-y-8 animate-fade-in">
                                <div>
                                    <h3 className="text-lg font-bold text-white border-b border-slate-700 pb-2 mb-4">Section 9: Authorization</h3>
                                    <div className="space-y-4">
                                        <div className="bg-slate-900 p-4 rounded border border-slate-700">
                                            <label className="block text-xs text-slate-400 uppercase font-bold mb-1">Permit Holder (Contractor/Tech)</label>
                                            <div className="flex justify-between items-center">
                                                <span className="text-white font-medium">{selectedPermit?.contractor || 'John Doe'}</span>
                                                <div className="flex items-center gap-2 text-green-500 text-sm">
                                                    <CheckCircle className="w-4 h-4" /> Signed
                                                </div>
                                            </div>
                                        </div>

                                        <div className="bg-slate-900 p-4 rounded border border-slate-700">
                                            <label className="block text-xs text-slate-400 uppercase font-bold mb-1">Authorized Person (Issuer)</label>
                                            {selectedPermit?.authorizedBy ? (
                                                <div className="flex justify-between items-center">
                                                    <span className="text-white font-medium">{selectedPermit.authorizedBy}</span>
                                                    <div className="flex items-center gap-2 text-green-500 text-sm">
                                                        <CheckCircle className="w-4 h-4" /> Authorized
                                                    </div>
                                                </div>
                                            ) : Number(selectedPermit?.status) === HSSE_LOOKUPS.PERMIT_STATUS.REQD ? (
                                                <div className="flex justify-between items-center">
                                                    <span className="text-slate-500 italic">Pending Authorization</span>
                                                    <button onClick={() => handlePtwStatusChange(HSSE_LOOKUPS.PERMIT_STATUS.ACTV)} className="bg-green-600 hover:bg-green-500 text-white px-4 py-2 rounded font-bold text-sm">Sign & Authorize</button>
                                                </div>
                                            ) : (
                                                <div className="text-slate-500 italic text-sm">Submit Permit first to enable Authorization.</div>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                {Number(selectedPermit?.status) === HSSE_LOOKUPS.PERMIT_STATUS.ACTV && (
                                    <div>
                                        <h3 className="text-lg font-bold text-white border-b border-slate-700 pb-2 mb-4">Section 12: Sign Off / Closure</h3>
                                        <div className="bg-slate-900 p-4 rounded border border-slate-700 space-y-4">
                                            <Checkbox label="Work completed successfully?" />
                                            <Checkbox label="Area cleaned and made safe?" />
                                            <Checkbox label="Isolations removed?" />
                                            <button onClick={() => handlePtwStatusChange(HSSE_LOOKUPS.PERMIT_STATUS.CLSD)} className="w-full bg-slate-700 hover:bg-slate-600 text-white py-3 rounded font-bold mt-2">Close Permit</button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                      </div>
                      
                      {/* Wizard Navigation */}
                      <div className="mt-6 pt-6 border-t border-slate-700 flex justify-between items-center pb-2">
                          <button 
                            onClick={handleBackPtw}
                            disabled={currentPtwIndex === 0}
                            className={`flex items-center gap-2 px-4 py-2 rounded font-bold transition ${currentPtwIndex === 0 ? 'opacity-0 cursor-default' : 'text-slate-400 hover:text-white hover:bg-slate-700'}`}
                          >
                            <ArrowLeft className="w-4 h-4" /> Back
                          </button>

                          {currentPtwIndex < ptwTabs.length - 1 ? (
                              <button 
                                onClick={handleNextPtw}
                                className="bg-titan-500 hover:bg-titan-600 text-white px-6 py-2 rounded font-bold flex items-center gap-2 transition shadow-lg shadow-titan-500/20"
                              >
                                Next Step <ArrowRight className="w-4 h-4" />
                              </button>
                          ) : (
                              // Final Step Action Button
                              Number(selectedPermit?.status) === HSSE_LOOKUPS.PERMIT_STATUS.DRFT ? (
                                <button 
                                    onClick={() => handlePtwStatusChange(HSSE_LOOKUPS.PERMIT_STATUS.REQD)}
                                    className="bg-purple-600 hover:bg-purple-500 text-white px-6 py-2 rounded font-bold flex items-center gap-2 transition shadow-lg shadow-purple-900/20"
                                >
                                    <FileCheck className="w-4 h-4" /> Submit Permit Request
                                </button>
                              ) : null
                          )}
                      </div>
                  </div>
              </div>
          </Modal>
      )}

      {showMocModal && (
         <Modal title={selectedMoc ? `Manage Change: ${selectedMoc.id}` : "Raise New Change (MOC)"} onClose={() => setShowMocModal(false)} size="xl">
            <div className="flex gap-6 h-[700px]">
                {/* Left Sidebar Wizard */}
                <div className="w-64 bg-slate-900 border-r border-slate-700 rounded-l-lg flex flex-col">
                    {[
                        { id: 'Definition', icon: FileText, label: '1. Scope & Def.', desc: 'Define the change' },
                        { id: 'Impact', icon: ShieldAlert, label: '2. Impact Analysis', desc: 'HSE & Technical' },
                        { id: 'Plan', icon: ListTodo, label: '3. Action Plan', desc: 'Pre-startup items' },
                        { id: 'Auth', icon: FileSignature, label: '4. Authorization', desc: 'Approvals & Close' },
                    ].map((step, idx) => (
                        <button 
                          key={step.id}
                          onClick={() => setMocModalTab(step.id as any)} 
                          className={`text-left px-6 py-4 border-l-4 transition flex gap-3 ${mocModalTab === step.id ? 'border-titan-500 bg-slate-800 text-white' : 'border-transparent text-slate-400 hover:bg-slate-800'}`}
                        >
                            <step.icon className={`w-5 h-5 mt-1 ${mocModalTab === step.id ? 'text-titan-500' : 'text-slate-500'}`} />
                            <div>
                                <div className="font-bold text-sm">{step.label}</div>
                                <div className="text-xs opacity-70">{step.desc}</div>
                            </div>
                        </button>
                    ))}

                    <div className="mt-auto p-4 border-t border-slate-700">
                        <div className="text-xs text-slate-500 mb-1">Current Status</div>
                        <StatusBadge status={selectedMoc?.status ? getLookupLabel('moc_status', selectedMoc.status) : 'Draft'} />
                    </div>
                </div>

                {/* Content Area */}
                <div className="flex-1 overflow-y-auto pr-2 flex flex-col">
                    <div className="flex-1">
                        {mocModalTab === 'Definition' && (
                            <div className="space-y-6 animate-fade-in">
                                <h3 className="text-lg font-bold text-white border-b border-slate-700 pb-2">Change Definition</h3>
                                <div className="grid grid-cols-2 gap-4">
                                    <Input label="Title of Change" defaultValue={selectedMoc?.title} placeholder="e.g. Pump Replacement P-101" />
                                    <div className="grid grid-cols-2 gap-4">
                                        <Input label="MOC ID" value={selectedMoc?.id || "Auto-Generated"} disabled />
                                        <Input label="Date Raised" type="date" defaultValue={selectedMoc?.dateRaised} />
                                    </div>
                                </div>
                                <div className="grid grid-cols-2 gap-4">
                                    <Select label="Change Type" defaultValue={selectedMoc?.type} options={lookups['moc_type'] || []} />
                                    <Select label="Initiator" defaultValue={selectedMoc?.initiator || "Current User"} options={['Current User', 'Tech Dept', 'Ops Manager']} />
                                </div>
                                <div>
                                    <label className="block text-xs text-slate-400 uppercase font-bold mb-1">Description of Change</label>
                                    <textarea className="w-full bg-slate-900 border border-slate-700 text-white p-2 rounded focus:border-titan-500 outline-none h-24" defaultValue={selectedMoc?.description}></textarea>
                                </div>
                                <div>
                                    <label className="block text-xs text-slate-400 uppercase font-bold mb-1">Justification / Reason</label>
                                    <textarea className="w-full bg-slate-900 border border-slate-700 text-white p-2 rounded focus:border-titan-500 outline-none h-20" defaultValue={selectedMoc?.justification}></textarea>
                                </div>
                            </div>
                        )}

                        {mocModalTab === 'Impact' && (
                             <div className="space-y-6 animate-fade-in">
                                <h3 className="text-lg font-bold text-white border-b border-slate-700 pb-2">Impact & Risk Assessment</h3>
                                
                                <div className="bg-slate-900 p-4 rounded border border-slate-700">
                                    <h4 className="text-sm font-bold text-white mb-3">Does this change affect:</h4>
                                    <div className="grid grid-cols-2 gap-4">
                                        <Checkbox label="Process & Instrumentation Diagrams (P&IDs)" defaultChecked={selectedMoc?.impactAnalysis?.drawingsUpdate} />
                                        <Checkbox label="Standard Operating Procedures (SOPs)" defaultChecked={selectedMoc?.impactAnalysis?.proceduresUpdate} />
                                        <Checkbox label="Training / Competency Requirements" defaultChecked={selectedMoc?.impactAnalysis?.trainingRequired} />
                                        <Checkbox label="Regulatory / Legal Compliance" defaultChecked={selectedMoc?.impactAnalysis?.regulatoryImpact} />
                                        <Checkbox label="Environmental Discharge / Limits" defaultChecked={selectedMoc?.impactAnalysis?.envImpact} />
                                        <Checkbox label="Alarm Limits or Interlocks" />
                                    </div>
                                </div>

                                <div className="bg-slate-900 p-4 rounded border border-slate-700">
                                    <h4 className="text-sm font-bold text-white mb-3">Risk Assessment</h4>
                                    <div className="flex gap-4 items-center">
                                        <div className="flex-1">
                                            <Select label="Overall Risk Level" defaultValue={selectedMoc?.riskLevel} options={lookups['moc_risk_level'] || []} />
                                        </div>
                                        <div className="flex-1 bg-slate-800 p-3 rounded text-sm text-slate-400">
                                            *High/Critical risk changes require Plant Manager approval.
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {mocModalTab === 'Plan' && (
                             <div className="space-y-6 animate-fade-in">
                                <div className="flex justify-between items-center border-b border-slate-700 pb-2">
                                    <h3 className="text-lg font-bold text-white">Pre-Startup Action Plan</h3>
                                    <button className="text-xs bg-titan-500 hover:bg-titan-600 text-white px-2 py-1 rounded">+ Add Action</button>
                                </div>
                                
                                <div className="bg-slate-900 rounded border border-slate-700">
                                    <table className="w-full text-left text-sm">
                                        <thead className="text-slate-500 bg-slate-950 border-b border-slate-800">
                                            <tr>
                                                <th className="p-3">Task Description</th>
                                                <th className="p-3">Assignee</th>
                                                <th className="p-3">Due Date</th>
                                                <th className="p-3">Status</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-slate-800">
                                            {Array.isArray(selectedMoc?.actions) && selectedMoc.actions.map(action => (
                                                <tr key={action.id}>
                                                    <td className="p-3 text-white">{action.task}</td>
                                                    <td className="p-3 text-slate-400">{action.assignee}</td>
                                                    <td className="p-3 text-slate-400">{action.dueDate}</td>
                                                    <td className="p-3">
                                                        <span className={`px-2 py-0.5 rounded text-xs ${action.status === 'Complete' ? 'bg-green-900 text-green-400' : 'bg-yellow-900 text-yellow-400'}`}>
                                                            {action.status}
                                                        </span>
                                                    </td>
                                                </tr>
                                            ))}
                                            {(!Array.isArray(selectedMoc?.actions) || selectedMoc.actions.length === 0) && (
                                                <tr><td colSpan={4} className="p-4 text-center text-slate-500 italic">No actions defined yet.</td></tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )}

                        {mocModalTab === 'Auth' && (
                             <div className="space-y-6 animate-fade-in">
                                <h3 className="text-lg font-bold text-white border-b border-slate-700 pb-2">Authorization & Closure</h3>
                                
                                <div className="space-y-3">
                                    {Array.isArray(selectedMoc?.approvals) ? selectedMoc.approvals.map((app, idx) => (
                                        <div key={idx} className="bg-slate-900 p-4 rounded border border-slate-700 flex justify-between items-center">
                                            <div className="flex items-center gap-4">
                                                <div className={`p-2 rounded-full ${app.status === 'Approved' ? 'bg-green-900/50 text-green-500' : 'bg-slate-800 text-slate-500'}`}>
                                                    <UserCheck className="w-5 h-5" />
                                                </div>
                                                <div>
                                                    <div className="text-white font-bold">{app.role}</div>
                                                    <div className="text-sm text-slate-400">{app.name}</div>
                                                </div>
                                            </div>
                                            
                                            {app.status === 'Approved' ? (
                                                <div className="text-right">
                                                    <div className="text-green-500 text-sm font-bold flex items-center gap-1 justify-end">
                                                        <CheckCircle className="w-4 h-4"/> Approved
                                                    </div>
                                                    <div className="text-xs text-slate-500">{app.date}</div>
                                                </div>
                                            ) : Number(selectedMoc.status) === HSSE_LOOKUPS.MOC_STATUS.REVW ? (
                                                <div className="flex gap-2">
                                                    <button onClick={() => handleMocApproval(app.role, 'Approved')} className="px-3 py-1 bg-green-600 hover:bg-green-500 text-white text-xs rounded font-bold">Approve</button>
                                                    <button onClick={() => handleMocApproval(app.role, 'Rejected')} className="px-3 py-1 bg-red-600 hover:bg-red-500 text-white text-xs rounded font-bold">Reject</button>
                                                </div>
                                            ) : (
                                                <span className="text-sm text-slate-500 italic">Pending Review</span>
                                            )}
                                        </div>
                                    )) : (
                                        <div className="text-slate-500 italic">Approvers will be assigned upon submission to Review.</div>
                                    )}
                                </div>

                                {Number(selectedMoc?.status) === HSSE_LOOKUPS.MOC_STATUS.APPR && (
                                    <div className="bg-blue-900/20 border border-blue-900/50 p-4 rounded text-center">
                                        <h4 className="text-blue-400 font-bold mb-2">Ready for Implementation</h4>
                                        <p className="text-sm text-blue-200 mb-4">All approvals secured. You may proceed with the changes.</p>
                                        <button onClick={() => handleMocStatusChange(HSSE_LOOKUPS.MOC_STATUS.IMPL)} className="bg-blue-600 hover:bg-blue-500 text-white px-6 py-2 rounded font-bold">Start Implementation</button>
                                    </div>
                                )}
                                
                                {Number(selectedMoc?.status) === HSSE_LOOKUPS.MOC_STATUS.IMPL && (
                                     <div className="bg-slate-800 p-4 rounded border border-slate-700 text-center">
                                         <h4 className="text-white font-bold mb-2">Closure Sign-off</h4>
                                         <p className="text-sm text-slate-400 mb-4">Verify all action items are complete and PSSER conducted.</p>
                                         <button onClick={() => handleMocStatusChange(HSSE_LOOKUPS.MOC_STATUS.CLSD)} className="bg-green-600 hover:bg-green-500 text-white px-6 py-2 rounded font-bold">Close MOC</button>
                                     </div>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Navigation Footer */}
                    <div className="mt-6 pt-6 border-t border-slate-700 flex justify-between items-center pb-2">
                          <button 
                            onClick={handleBackMoc}
                            disabled={currentMocIndex === 0}
                            className={`flex items-center gap-2 px-4 py-2 rounded font-bold transition ${currentMocIndex === 0 ? 'opacity-0 cursor-default' : 'text-slate-400 hover:text-white hover:bg-slate-700'}`}
                          >
                            <ArrowLeft className="w-4 h-4" /> Back
                          </button>

                          {currentMocIndex < mocTabs.length - 1 ? (
                              <button 
                                onClick={handleNextMoc}
                                className="bg-titan-500 hover:bg-titan-600 text-white px-6 py-2 rounded font-bold flex items-center gap-2 transition shadow-lg shadow-titan-500/20"
                              >
                                Next Step <ArrowRight className="w-4 h-4" />
                              </button>
                          ) : (
                              // Final Step Action Button based on status
                              Number(selectedMoc?.status) === HSSE_LOOKUPS.MOC_STATUS.DRFT ? (
                                <button 
                                    onClick={() => handleMocStatusChange(HSSE_LOOKUPS.MOC_STATUS.REVW)}
                                    className="bg-purple-600 hover:bg-purple-500 text-white px-6 py-2 rounded font-bold flex items-center gap-2 transition shadow-lg shadow-purple-900/20"
                                >
                                    <FileCheck className="w-4 h-4" /> Submit for Review
                                </button>
                              ) : null
                          )}
                      </div>
                </div>
            </div>
         </Modal>
      )}

      {showJhaModal && (
          <Modal title={selectedJha?.id ? `Risk Assessment: ${selectedJha.id}` : "New JHA"} onClose={() => setShowJhaModal(false)} size="xl">
               <div className="flex gap-6 h-[700px]">
                  {/* Left Sidebar Wizard */}
                  <div className="w-64 bg-slate-900 border-r border-slate-700 rounded-l-lg flex flex-col">
                      {[
                          { id: 'Scope', icon: FileText, label: '1. Scope & Task', desc: 'Define activity' },
                          { id: 'Hazards', icon: AlertTriangle, label: '2. Hazards', desc: 'Identify dangers' },
                          { id: 'Risk', icon: Sliders, label: '3. Initial Risk', desc: 'Assess raw risk' },
                          { id: 'Controls', icon: ShieldAlert, label: '4. Controls', desc: 'Mitigation plan' },
                          { id: 'Review', icon: FileSignature, label: '5. Review', desc: 'Sign-off' },
                      ].map((step, idx) => (
                          <button 
                            key={step.id}
                            onClick={() => setJhaModalTab(step.id as any)} 
                            className={`text-left px-6 py-4 border-l-4 transition flex gap-3 ${jhaModalTab === step.id ? 'border-titan-500 bg-slate-800 text-white' : 'border-transparent text-slate-400 hover:bg-slate-800'}`}
                          >
                              <step.icon className={`w-5 h-5 mt-1 ${jhaModalTab === step.id ? 'text-titan-500' : 'text-slate-500'}`} />
                              <div>
                                  <div className="font-bold text-sm">{step.label}</div>
                                  <div className="text-xs opacity-70">{step.desc}</div>
                              </div>
                          </button>
                      ))}

                      <div className="mt-auto p-4 border-t border-slate-700">
                          <div className="text-xs text-slate-500 mb-1">Status</div>
                          <StatusBadge status={selectedJha?.status ? getLookupLabel('jha_status', selectedJha.status) : 'Draft'} />
                      </div>
                  </div>
                  
                  {/* Content Area */}
                  <div className="flex-1 overflow-y-auto pr-2 flex flex-col">
                      <div className="flex-1">
                          {jhaModalTab === 'Scope' && (
                              <div className="space-y-6 animate-fade-in">
                                  <h3 className="text-lg font-bold text-white border-b border-slate-700 pb-2">Task Definition</h3>
                                  <div className="grid grid-cols-2 gap-4">
                                      <Input label="Task / Activity Name" value={selectedJha?.taskName} onChange={(e: any) => handleUpdateJha({taskName: e.target.value})} placeholder="e.g. Compressor Service" />
                                      <Input label="JHA Reference" value={selectedJha?.id} disabled />
                                  </div>
                                  <div className="grid grid-cols-2 gap-4">
                                      <Input label="Department" value={selectedJha?.department} onChange={(e: any) => handleUpdateJha({department: e.target.value})} />
                                      <Input label="Assessment Date" type="date" value={selectedJha?.reviewDate} onChange={(e: any) => handleUpdateJha({reviewDate: e.target.value})} />
                                  </div>
                                  <div>
                                      <label className="block text-xs text-slate-400 uppercase font-bold mb-1">Assessment Team Members</label>
                                      <textarea className="w-full bg-slate-900 border border-slate-700 text-white p-2 rounded focus:border-titan-500 outline-none h-24" placeholder="List names of team members involved..." value={selectedJha?.teamMembers || ''} onChange={(e) => handleUpdateJha({teamMembers: e.target.value})}></textarea>
                                  </div>
                              </div>
                          )}

                          {jhaModalTab === 'Hazards' && (
                              <div className="space-y-6 animate-fade-in">
                                  <h3 className="text-lg font-bold text-white border-b border-slate-700 pb-2">Hazard Identification</h3>
                                  <p className="text-sm text-slate-400 mb-4">Select all potential hazards associated with this task.</p>
                                  <div className="grid grid-cols-2 md:grid-cols-3 gap-y-3 gap-x-6">
                                      {HAZARDS.map(h => (
                                          <Checkbox 
                                            key={h} 
                                            label={h} 
                                            checked={selectedJha?.hazards?.includes(h)} 
                                            onChange={() => handleToggleHazard(h)} 
                                          />
                                      ))}
                                  </div>
                              </div>
                          )}

                          {jhaModalTab === 'Risk' && (
                              <div className="space-y-6 animate-fade-in flex flex-col items-center">
                                  <div className="w-full text-left">
                                    <h3 className="text-lg font-bold text-white border-b border-slate-700 pb-2 mb-6">Initial Risk Assessment</h3>
                                  </div>
                                  <InteractiveRiskMatrix 
                                      selected={selectedJha?.initialRisk || ''} 
                                      onSelect={(val) => handleJhaRiskSelect('Initial', val)} 
                                  />
                                  <div className="mt-8 bg-slate-900 p-4 rounded border border-slate-700 max-w-lg text-center">
                                      <h4 className="text-white font-bold mb-2">Risk Rating Guide</h4>
                                      <p className="text-sm text-slate-400">Rate the risk based on potential severity and probability of occurrence <span className="text-white font-bold uppercase">without controls</span>.</p>
                                  </div>
                              </div>
                          )}

                          {jhaModalTab === 'Controls' && (
                              <div className="space-y-8 animate-fade-in">
                                  <div>
                                      <h3 className="text-lg font-bold text-white border-b border-slate-700 pb-2 mb-4">Required Controls</h3>
                                      <div className="bg-slate-900 p-4 rounded border border-slate-700 space-y-4">
                                          <div>
                                              <label className="block text-xs text-slate-400 uppercase font-bold mb-1">Engineering & Admin Controls</label>
                                              <textarea 
                                                className="w-full bg-slate-800 border border-slate-600 text-white p-2 rounded focus:border-titan-500 outline-none h-24" 
                                                placeholder="e.g. Install barricades, Use lifting aids, Lock out equipment..."
                                                value={selectedJha?.controls?.join('\n') || ''}
                                                onChange={(e) => handleUpdateJha({ controls: e.target.value.split('\n') })}
                                              ></textarea>
                                              <div className="text-xs text-slate-500 mt-1">Enter each control on a new line.</div>
                                          </div>
                                      </div>
                                  </div>

                                  <div>
                                      <h3 className="text-lg font-bold text-white border-b border-slate-700 pb-2 mb-4">Personal Protective Equipment (PPE)</h3>
                                      <div className="grid grid-cols-2 md:grid-cols-3 gap-y-3 gap-x-6">
                                          {PPE.map(p => (
                                              <Checkbox 
                                                key={p} 
                                                label={p} 
                                                checked={selectedJha?.ppe?.includes(p)}
                                                onChange={() => handleTogglePPE(p)}
                                              />
                                          ))}
                                      </div>
                                  </div>
                              </div>
                          )}

                          {jhaModalTab === 'Review' && (
                              <div className="space-y-8 animate-fade-in">
                                  <div>
                                      <h3 className="text-lg font-bold text-white border-b border-slate-700 pb-2 mb-6">Residual Risk & Approval</h3>
                                      <div className="flex flex-col md:flex-row gap-8 items-start">
                                          <div className="flex-1 w-full">
                                              <h4 className="text-sm font-bold text-slate-300 mb-4 text-center">Residual Risk (With Controls)</h4>
                                              <InteractiveRiskMatrix 
                                                  selected={selectedJha?.residualRisk || ''} 
                                                  onSelect={(val) => handleJhaRiskSelect('Residual', val)} 
                                              />
                                          </div>
                                          <div className="flex-1 w-full space-y-4">
                                              <div className="bg-slate-900 p-4 rounded border border-slate-700">
                                                  <div className="text-xs text-slate-500 uppercase font-bold mb-2">Summary</div>
                                                  <div className="flex justify-between items-center mb-1">
                                                      <span className="text-slate-400">Initial Risk:</span>
                                                      <RiskBadge level={selectedJha?.initialRisk || 'Low'} />
                                                  </div>
                                                  <div className="flex justify-between items-center">
                                                      <span className="text-slate-400">Residual Risk:</span>
                                                      <RiskBadge level={selectedJha?.residualRisk || 'Low'} />
                                                  </div>
                                              </div>
                                              
                                              {Number(selectedJha?.status) === HSSE_LOOKUPS.JHA_STATUS.DRFT ? (
                                                  <div className="bg-slate-800 p-4 rounded border border-slate-700">
                                                      <h4 className="text-white font-bold mb-2">Authorization</h4>
                                                      <p className="text-xs text-slate-400 mb-4">By approving, you confirm that all hazards have been identified and adequate controls are in place.</p>
                                                      <button 
                                                        onClick={() => {
                                                            handleUpdateJha({ status: HSSE_LOOKUPS.JHA_STATUS.ACTV, approvalDate: new Date().toISOString().split('T')[0], approver: 'Current User' });
                                                            setShowJhaModal(false);
                                                        }} 
                                                        className="w-full bg-green-600 hover:bg-green-500 text-white py-2 rounded font-bold shadow-lg shadow-green-900/20"
                                                      >
                                                          Approve & Activate JHA
                                                      </button>
                                                  </div>
                                              ) : (
                                                  <div className="bg-green-900/20 border border-green-900/50 p-4 rounded text-center">
                                                      <CheckCircle className="w-8 h-8 text-green-500 mx-auto mb-2" />
                                                      <div className="text-green-400 font-bold">JHA Active</div>
                                                      <div className="text-xs text-green-300/70">Approved by {selectedJha?.approver} on {selectedJha?.approvalDate}</div>
                                                  </div>
                                              )}
                                          </div>
                                      </div>
                                  </div>
                              </div>
                          )}
                      </div>

                       {/* Navigation Footer */}
                      <div className="mt-6 pt-6 border-t border-slate-700 flex justify-between items-center pb-2">
                          <button 
                            onClick={handleBackJha}
                            disabled={currentJhaIndex === 0}
                            className={`flex items-center gap-2 px-4 py-2 rounded font-bold transition ${currentJhaIndex === 0 ? 'opacity-0 cursor-default' : 'text-slate-400 hover:text-white hover:bg-slate-700'}`}
                          >
                            <ArrowLeft className="w-4 h-4" /> Back
                          </button>

                          {currentJhaIndex < jhaTabs.length - 1 ? (
                              <button 
                                onClick={handleNextJha}
                                className="bg-titan-500 hover:bg-titan-600 text-white px-6 py-2 rounded font-bold flex items-center gap-2 transition shadow-lg shadow-titan-500/20"
                              >
                                Next Step <ArrowRight className="w-4 h-4" />
                              </button>
                          ) : (
                              <button 
                                onClick={() => setShowJhaModal(false)}
                                className="bg-slate-700 hover:bg-slate-600 text-white px-6 py-2 rounded font-bold flex items-center gap-2"
                              >
                                Close Wizard
                              </button>
                          )}
                      </div>
                  </div>
               </div>
          </Modal>
      )}
      </>
      )}

    </div>
  );
};

export const DocControlView: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'Dashboard' | 'Register'>('Dashboard');
  const [docs, setDocs] = useState<TitanDocument[]>(initialDocs);
  const [showDocModal, setShowDocModal] = useState(false);
  const [selectedDoc, setSelectedDoc] = useState<TitanDocument | null>(null);
  
  // Document Editing & State
  const [docEditMode, setDocEditMode] = useState(false);
  const [sourceType, setSourceType] = useState<'editor' | 'upload' | 'link'>('editor');
  const [modalTab, setModalTab] = useState<'Meta' | 'Content' | 'Workflow'>('Meta');

  const handleOpenDoc = (doc?: TitanDocument) => {
      if (doc) {
          setSelectedDoc(doc);
          setDocEditMode(false); // View mode by default
          setSourceType(doc.history[0]?.fileUrl ? (doc.history[0].fileUrl.startsWith('http') ? 'link' : 'upload') : 'editor');
      } else {
         // New Document
         setSelectedDoc({
            id: 'NEW-DOC',
            title: '',
            category: 'Policy',
            department: 'Operations',
            owner: 'Current User',
            currentVersion: '0.1',
            status: 'Draft',
            createdDate: new Date().toISOString().split('T')[0],
            lastReviewDate: '',
            nextReviewDate: '',
            confidentiality: 'Internal',
            acknowledgmentRequired: false,
            acknowledgmentCount: 0,
            history: [],
            approvals: []
         } as TitanDocument);
         setDocEditMode(true);
      }
      setModalTab('Meta');
      setShowDocModal(true);
  };

  const handleSaveDoc = (e: React.FormEvent) => {
      e.preventDefault();
      if(!selectedDoc) return;
      
      const newDoc = { ...selectedDoc };
      // Simulate ID creation
      if (newDoc.id === 'NEW-DOC') {
          newDoc.id = `DOC-${Date.now().toString().slice(-4)}`;
          // Initial History Entry
          newDoc.history = [{
              version: '0.1',
              date: new Date().toISOString().split('T')[0],
              changedBy: 'Current User',
              changeSummary: 'Document Created',
              fileUrl: sourceType === 'upload' ? 'uploaded_file.pdf' : sourceType === 'link' ? 'https://sharepoint.com/doc123' : undefined
          }];
          setDocs([newDoc, ...docs]);
      } else {
          // Update Existing
          setDocs(docs.map(d => d.id === newDoc.id ? newDoc : d));
      }
      setShowDocModal(false);
  };

  const updateDocState = (updates: Partial<TitanDocument>) => {
      if (!selectedDoc) return;
      setSelectedDoc({ ...selectedDoc, ...updates });
  };

  // Workflow Processor
  const promoteWorkflow = (action: 'Submit' | 'Approve' | 'Reject' | 'Archive' | 'Revise') => {
      if (!selectedDoc) return;
      let newStatus: DocStatus = selectedDoc.status;
      let newVersion = selectedDoc.currentVersion;
      let historyEntry: DocVersion | null = null;
      let approvalEntry: DocApproval | null = null;

      const user = 'Current User';
      const today = new Date().toISOString().split('T')[0];

      if (action === 'Submit') {
          newStatus = 'Review';
          historyEntry = { version: newVersion, date: today, changedBy: user, changeSummary: 'Submitted for Review' };
      } else if (action === 'Approve') {
          if (selectedDoc.status === 'Review') {
              newStatus = 'Pending Approval';
              approvalEntry = { role: 'Reviewer', name: user, status: 'Approved', date: today, comment: 'Content verified' };
          } else if (selectedDoc.status === 'Pending Approval') {
              newStatus = 'Active';
              newVersion = (Math.floor(parseFloat(selectedDoc.currentVersion)) + 1).toFixed(1); // Increment Major
              approvalEntry = { role: 'Approver', name: user, status: 'Approved', date: today, comment: 'Approved for release' };
              historyEntry = { version: newVersion, date: today, changedBy: user, changeSummary: 'Document Released' };
          }
      } else if (action === 'Reject') {
          newStatus = 'Draft'; 
          historyEntry = { version: newVersion, date: today, changedBy: user, changeSummary: 'Rejected - Returned to Draft' };
      } else if (action === 'Archive') {
          newStatus = 'Archived';
          historyEntry = { version: newVersion, date: today, changedBy: user, changeSummary: 'Document Archived' };
      } else if (action === 'Revise') {
          // Create new draft version
          newStatus = 'Draft';
          newVersion = (parseFloat(newVersion) + 0.1).toFixed(1);
          setDocEditMode(true); // Enable editing
      }

      // Update State
      const updatedDoc = { 
          ...selectedDoc, 
          status: newStatus, 
          currentVersion: newVersion,
          history: historyEntry ? [historyEntry, ...selectedDoc.history] : selectedDoc.history,
          approvals: approvalEntry ? [...selectedDoc.approvals, approvalEntry] : selectedDoc.approvals
      };
      
      setSelectedDoc(updatedDoc);
      // Also update main list immediately for better UX
      setDocs(docs.map(d => d.id === updatedDoc.id ? updatedDoc : d));
  };

  return (
    <div className="animate-fade-in">
       <div className="flex justify-between items-center mb-6 min-h-[4rem]">
          <h2 className="text-xl font-bold text-white flex items-center gap-2">
              <FileCheck className="w-6 h-6 text-titan-500" />
              Document Control Center
          </h2>
          <div className="flex gap-3">
              <button 
                onClick={() => setActiveTab('Dashboard')} 
                className={`px-4 py-2 rounded font-medium text-sm transition ${activeTab === 'Dashboard' ? 'bg-slate-700 text-white' : 'text-slate-400 hover:text-white'}`}
              >
                  Dashboard
              </button>
              <button 
                onClick={() => setActiveTab('Register')} 
                className={`px-4 py-2 rounded font-medium text-sm transition ${activeTab === 'Register' ? 'bg-slate-700 text-white' : 'text-slate-400 hover:text-white'}`}
              >
                  Register
              </button>
              <button onClick={() => handleOpenDoc()} className="bg-titan-500 hover:bg-titan-600 text-white px-4 py-2 rounded font-bold flex items-center gap-2 text-sm">
                  <Plus className="w-4 h-4" /> New Document
              </button>
          </div>
       </div>

       {activeTab === 'Dashboard' && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 animate-fade-in">
              <div className="bg-slate-800 p-5 rounded-lg border border-slate-700">
                  <div className="text-slate-400 text-xs uppercase font-bold mb-2">Pending My Approval</div>
                  <div className="text-3xl font-bold text-white">{docs.filter(d => d.status === 'Pending Approval').length}</div>
              </div>
              <div className="bg-slate-800 p-5 rounded-lg border border-slate-700">
                  <div className="text-slate-400 text-xs uppercase font-bold mb-2">In Draft / Review</div>
                  <div className="text-3xl font-bold text-white">{docs.filter(d => d.status === 'Draft' || d.status === 'Review').length}</div>
              </div>
              <div className="bg-slate-800 p-5 rounded-lg border border-slate-700">
                  <div className="text-slate-400 text-xs uppercase font-bold mb-2">Total Active</div>
                  <div className="text-3xl font-bold text-white">{docs.filter(d => d.status === 'Active').length}</div>
              </div>
              <div className="bg-slate-800 p-5 rounded-lg border border-slate-700">
                  <div className="text-slate-400 text-xs uppercase font-bold mb-2">Expiring Soon</div>
                  <div className="text-3xl font-bold text-white">1</div>
                  <div className="text-xs text-red-400 mt-1">Renewals Required</div>
              </div>
          </div>
       )}

       {activeTab === 'Register' && (
           <div className="bg-slate-800 rounded-lg border border-slate-700 overflow-hidden animate-fade-in">
              <table className="w-full text-left">
                 <thead className="bg-slate-900 text-slate-400 text-xs uppercase">
                    <tr>
                       <th className="px-6 py-4">Doc ID</th>
                       <th className="px-6 py-4">Title</th>
                       <th className="px-6 py-4">Category</th>
                       <th className="px-6 py-4">Version</th>
                       <th className="px-6 py-4">Owner</th>
                       <th className="px-6 py-4">Status</th>
                       <th className="px-6 py-4">Actions</th>
                    </tr>
                 </thead>
                 <tbody className="divide-y divide-slate-700">
                    {docs.map(doc => (
                       <tr key={doc.id} className="hover:bg-slate-750 transition">
                          <td className="px-6 py-4 font-mono text-slate-400">{doc.id}</td>
                          <td className="px-6 py-4 text-white font-medium">
                              {doc.title}
                              {doc.confidentiality === 'Confidential' && <Lock className="w-3 h-3 inline ml-2 text-red-400" />}
                          </td>
                          <td className="px-6 py-4 text-slate-300">{doc.category}</td>
                          <td className="px-6 py-4 text-white">v{doc.currentVersion}</td>
                          <td className="px-6 py-4 text-slate-400">{doc.owner}</td>
                          <td className="px-6 py-4"><StatusBadge status={doc.status} /></td>
                          <td className="px-6 py-4">
                              <button onClick={() => handleOpenDoc(doc)} className="text-titan-500 hover:text-white font-medium text-xs border border-titan-500/30 px-3 py-1 rounded hover:bg-titan-500 hover:border-transparent transition">Manage</button>
                          </td>
                       </tr>
                    ))}
                 </tbody>
              </table>
           </div>
       )}

       {/* --- DOCUMENT MODAL --- */}
       {showDocModal && selectedDoc && (
          <Modal title={selectedDoc.id === 'NEW-DOC' ? 'Create New Document' : `Manage Document: ${selectedDoc.id}`} onClose={() => setShowDocModal(false)} size="xl">
             <div className="flex gap-6 h-[650px]">
                 {/* Left Sidebar Actions */}
                 <div className="w-64 bg-slate-900 border-r border-slate-700 rounded-l-lg flex flex-col p-4">
                     <div className="mb-6 text-center">
                         <div className="w-16 h-16 bg-slate-800 rounded-full flex items-center justify-center mx-auto mb-3 border-2 border-titan-500/50">
                             <FileText className="w-8 h-8 text-titan-500" />
                         </div>
                         <StatusBadge status={selectedDoc.status} />
                         <div className="text-xs text-slate-500 mt-2">Current Version: <span className="text-white font-bold">v{selectedDoc.currentVersion}</span></div>
                     </div>

                     <div className="space-y-2 mb-6">
                         {[
                             { id: 'Meta', label: '1. Metadata', icon: ListTodo },
                             { id: 'Content', label: '2. Source File', icon: FileUp },
                             { id: 'Workflow', label: '3. Workflow', icon: Activity },
                         ].map(tab => (
                             <button 
                                key={tab.id}
                                onClick={() => setModalTab(tab.id as any)}
                                className={`w-full text-left px-4 py-3 flex items-center gap-3 rounded transition ${modalTab === tab.id ? 'bg-slate-800 text-white border-l-4 border-titan-500' : 'text-slate-400 hover:bg-slate-800 hover:text-white'}`}
                             >
                                 <tab.icon className="w-4 h-4" /> {tab.label}
                             </button>
                         ))}
                     </div>

                     {/* Quick Actions (Bottom) */}
                     <div className="mt-auto pt-6 border-t border-slate-800 space-y-2">
                         {selectedDoc.status === 'Active' && (
                             <>
                                <button onClick={() => promoteWorkflow('Revise')} className="w-full bg-titan-500 hover:bg-titan-600 text-white py-2 rounded font-bold text-sm flex items-center justify-center gap-2">
                                    <RotateCcw className="w-4 h-4" /> New Revision
                                </button>
                                <button onClick={() => promoteWorkflow('Archive')} className="w-full bg-slate-800 hover:bg-slate-700 text-slate-300 py-2 rounded font-medium text-sm flex items-center justify-center gap-2">
                                    <Archive className="w-4 h-4" /> Archive Doc
                                </button>
                             </>
                         )}
                         {selectedDoc.status !== 'Active' && !docEditMode && (
                             <button onClick={() => setDocEditMode(true)} className="w-full bg-slate-700 hover:bg-slate-600 text-white py-2 rounded font-medium text-sm">
                                 Edit Metadata
                             </button>
                         )}
                     </div>
                 </div>

                 {/* Main Content */}
                 <form onSubmit={handleSaveDoc} className="flex-1 overflow-y-auto pr-2">
                     
                     {modalTab === 'Meta' && (
                         <div className="space-y-6 animate-fade-in">
                             <h3 className="text-lg font-bold text-white border-b border-slate-700 pb-2">Document Metadata</h3>
                             
                             <div className="grid grid-cols-2 gap-4">
                                <Input label="Document Title" value={selectedDoc.title} onChange={(e: any) => updateDocState({title: e.target.value})} disabled={!docEditMode} />
                                <Select label="Category" value={selectedDoc.category} onChange={(e: any) => updateDocState({category: e.target.value})} options={['Policy', 'SOP', 'Form', 'Work Instruction', 'Manual']} disabled={!docEditMode} />
                             </div>
                             <div className="grid grid-cols-2 gap-4">
                                <Select label="Owner / Author" value={selectedDoc.owner} onChange={(e: any) => updateDocState({owner: e.target.value})} options={['Current User', 'M. Scott', 'S. Nkosi']} disabled={!docEditMode} />
                                <Select label="Department" value={selectedDoc.department} onChange={(e: any) => updateDocState({department: e.target.value})} options={['Operations', 'HSSE', 'HR', 'Engineering', 'Finance']} disabled={!docEditMode} />
                             </div>
                             <div className="grid grid-cols-2 gap-4">
                                <Select label="Confidentiality" value={selectedDoc.confidentiality} onChange={(e: any) => updateDocState({confidentiality: e.target.value})} options={['Public', 'Internal', 'Confidential', 'Restricted']} disabled={!docEditMode} />
                                <Input label="Next Review Date" type="date" value={selectedDoc.nextReviewDate} onChange={(e: any) => updateDocState({nextReviewDate: e.target.value})} disabled={!docEditMode} />
                             </div>
                             
                             <div className="bg-slate-900 p-4 rounded border border-slate-700">
                                 <Checkbox label="Mandatory Acknowledgment Required?" checked={selectedDoc.acknowledgmentRequired} onChange={(e: any) => updateDocState({acknowledgmentRequired: e.target.checked})} disabled={!docEditMode} />
                                 <p className="text-xs text-slate-500 mt-2 ml-6">If checked, all employees in the department must digitally sign that they have read this document.</p>
                             </div>
                         </div>
                     )}

                     {modalTab === 'Content' && (
                         <div className="space-y-6 animate-fade-in">
                             <h3 className="text-lg font-bold text-white border-b border-slate-700 pb-2">Content Source</h3>
                             
                             <div className="flex gap-4 mb-6">
                                 {[
                                     { id: 'editor', label: 'Online Editor', icon: PenTool },
                                     { id: 'upload', label: 'Upload File', icon: FileUp },
                                     { id: 'link', label: 'External Link', icon: Link },
                                 ].map(type => (
                                     <button
                                        key={type.id}
                                        type="button"
                                        disabled={!docEditMode && selectedDoc.id !== 'NEW-DOC'}
                                        onClick={() => setSourceType(type.id as any)}
                                        className={`flex-1 py-4 px-2 rounded border flex flex-col items-center gap-2 transition ${
                                            sourceType === type.id 
                                            ? 'bg-titan-500/10 border-titan-500 text-titan-500' 
                                            : 'bg-slate-900 border-slate-700 text-slate-400 hover:bg-slate-800'
                                        }`}
                                     >
                                         <type.icon className="w-6 h-6" />
                                         <span className="text-sm font-bold">{type.label}</span>
                                     </button>
                                 ))}
                             </div>

                             {sourceType === 'editor' && (
                                 <div className="bg-slate-900 p-4 rounded border border-slate-700 min-h-[200px] flex items-center justify-center text-slate-500">
                                     <div className="text-center">
                                         <PenTool className="w-8 h-8 mx-auto mb-2 opacity-50" />
                                         <p>Rich Text Editor would appear here.</p>
                                     </div>
                                 </div>
                             )}

                             {sourceType === 'upload' && (
                                 <div className="border-2 border-dashed border-slate-600 rounded-lg p-8 flex flex-col items-center justify-center hover:bg-slate-800/50 transition cursor-pointer">
                                     <Upload className="w-10 h-10 text-titan-500 mb-4" />
                                     <h4 className="text-white font-bold mb-1">Click to upload or drag and drop</h4>
                                     <p className="text-slate-400 text-sm">PDF, DOCX, XLSX (Max 10MB)</p>
                                     {selectedDoc.history[0]?.fileUrl && !docEditMode && (
                                         <div className="mt-4 bg-slate-900 px-4 py-2 rounded text-green-400 flex items-center gap-2">
                                             <FileCheck className="w-4 h-4" /> Current: {selectedDoc.history[0].fileUrl}
                                         </div>
                                     )}
                                 </div>
                             )}

                             {sourceType === 'link' && (
                                 <div className="space-y-4">
                                     <div className="bg-blue-900/20 p-4 rounded border border-blue-900/50 text-blue-300 text-sm flex gap-3">
                                         <Globe className="w-5 h-5 shrink-0" />
                                         <div>
                                             Link to external document management systems (SharePoint, OneDrive, Google Drive). 
                                             Ensure permissions are set correctly on the target platform.
                                         </div>
                                     </div>
                                     <Input label="External Document URL" placeholder="https://..." defaultValue={selectedDoc.history[0]?.fileUrl?.startsWith('http') ? selectedDoc.history[0].fileUrl : ''} disabled={!docEditMode} />
                                 </div>
                             )}
                         </div>
                     )}

                     {modalTab === 'Workflow' && (
                         <div className="space-y-6 animate-fade-in">
                             <h3 className="text-lg font-bold text-white border-b border-slate-700 pb-2">Approval Workflow</h3>
                             
                             {/* Current Status Action */}
                             <div className="bg-slate-900 p-5 rounded border border-slate-700 mb-6">
                                 <div className="flex justify-between items-center mb-4">
                                     <span className="text-sm text-slate-400 uppercase font-bold">Current State</span>
                                     <StatusBadge status={selectedDoc.status} />
                                 </div>
                                 
                                 <div className="flex gap-3">
                                     {selectedDoc.status === 'Draft' && (
                                         <button type="button" onClick={() => promoteWorkflow('Submit')} className="flex-1 bg-blue-600 hover:bg-blue-500 text-white py-2 rounded font-bold transition">Submit for Review</button>
                                     )}
                                     
                                     {selectedDoc.status === 'Review' && (
                                         <>
                                            <button type="button" onClick={() => promoteWorkflow('Approve')} className="flex-1 bg-green-600 hover:bg-green-500 text-white py-2 rounded font-bold transition">Review Passed</button>
                                            <button type="button" onClick={() => promoteWorkflow('Reject')} className="flex-1 bg-red-600 hover:bg-red-500 text-white py-2 rounded font-bold transition">Reject</button>
                                         </>
                                     )}

                                     {selectedDoc.status === 'Pending Approval' && (
                                         <>
                                            <button type="button" onClick={() => promoteWorkflow('Approve')} className="flex-1 bg-green-600 hover:bg-green-500 text-white py-2 rounded font-bold transition flex items-center justify-center gap-2"><FileSignature className="w-4 h-4"/> Final Approval & Publish</button>
                                            <button type="button" onClick={() => promoteWorkflow('Reject')} className="flex-1 bg-red-600 hover:bg-red-500 text-white py-2 rounded font-bold transition">Reject</button>
                                         </>
                                     )}

                                     {selectedDoc.status === 'Active' && (
                                         <div className="text-center w-full text-slate-500 italic text-sm">
                                             Document is live. Use "New Revision" to update.
                                         </div>
                                     )}
                                 </div>
                             </div>

                             {/* History List */}
                             <div className="space-y-4">
                                 <h4 className="text-sm font-bold text-white uppercase">History & Approvals</h4>
                                 {selectedDoc.history.map((hist, i) => (
                                     <div key={i} className="flex gap-4 p-3 bg-slate-900 rounded border border-slate-700">
                                         <div className="text-center min-w-[50px]">
                                             <div className="text-sm font-bold text-white">v{hist.version}</div>
                                             <div className="text-xs text-slate-500">{hist.date}</div>
                                         </div>
                                         <div>
                                             <div className="text-white text-sm font-medium">{hist.changeSummary}</div>
                                             <div className="text-xs text-slate-400">By: {hist.changedBy}</div>
                                         </div>
                                     </div>
                                 ))}
                                 {selectedDoc.approvals.length > 0 && (
                                     <div className="mt-4 pt-4 border-t border-slate-800">
                                         {selectedDoc.approvals.map((app, i) => (
                                             <div key={i} className="flex justify-between items-center text-sm mb-2">
                                                 <span className="text-slate-400">{app.role}: <span className="text-white">{app.name}</span></span>
                                                 <span className="text-green-500 flex items-center gap-1"><CheckCircle className="w-3 h-3" /> Approved ({app.date})</span>
                                             </div>
                                         ))}
                                     </div>
                                 )}
                             </div>
                         </div>
                     )}

                     {/* Save / Close Footer */}
                     <div className="flex justify-end pt-4 mt-6 border-t border-slate-700 gap-3">
                        <button type="button" onClick={() => setShowDocModal(false)} className="px-4 py-2 text-slate-400 hover:text-white">Close</button>
                        {docEditMode && (
                            <button type="submit" className="bg-titan-500 hover:bg-titan-600 text-white px-6 py-2 rounded font-bold">Save Changes</button>
                        )}
                     </div>
                 </form>
             </div>
          </Modal>
       )}
    </div>
  );
};
